import React, { useState, useEffect } from 'react';
import './App.css';
import PrivacyPolicy from './PrivacyPolicy';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

// Utility function to save token in multiple places for iOS WebView persistence
const saveTokenPersistently = (token) => {
  if (!token) return;
  
  // Save in localStorage (primary)
  localStorage.setItem('token', token);
  
  // Save in sessionStorage (backup)
  sessionStorage.setItem('token', token);
  
  // Save in cookie (better persistence on iOS WebView)
  document.cookie = `auth_token=${token}; path=/; max-age=31536000; SameSite=Lax`;
  
  console.log('✅ Token saved persistently');
};

// Utility function to retrieve token from any available source
const getTokenFromAnySource = () => {
  // Try localStorage first
  let token = localStorage.getItem('token');
  if (token) return token;
  
  // Try sessionStorage
  token = sessionStorage.getItem('token');
  if (token) {
    console.log('✅ Token recovered from sessionStorage');
    saveTokenPersistently(token);
    return token;
  }
  
  // Try cookie
  const cookies = document.cookie.split(';');
  for (let cookie of cookies) {
    const [name, value] = cookie.trim().split('=');
    if (name === 'auth_token' && value) {
      console.log('✅ Token recovered from cookie');
      saveTokenPersistently(value);
      return value;
    }
  }
  
  return null;
};

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentView, setCurrentView] = useState('login');
  const [showPrivacyPolicy, setShowPrivacyPolicy] = useState(false);

  useEffect(() => {
    // Check if user is logged in from any storage
    const token = getTokenFromAnySource();
    if (token) {
      fetchCurrentUser(token);
    } else {
      setLoading(false);
    }

    // Listen for mobile auth token injection
    const handleMobileAuth = (event) => {
      console.log('📱 Mobile auth token received:', event.detail);
      const mobileToken = localStorage.getItem('token');
      if (mobileToken) {
        console.log('📱 Auto-logging in with mobile token...');
        saveTokenPersistently(mobileToken); // Save in all places
        fetchCurrentUser(mobileToken);
      }
    };

    // Listen for page visibility changes (when app comes back to foreground)
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        console.log('📱 App became visible, checking token...');
        const currentToken = getTokenFromAnySource();
        if (currentToken && !user) {
          console.log('✅ Token recovered, re-authenticating...');
          fetchCurrentUser(currentToken);
        }
      }
    };

    window.addEventListener('mobile-auth-injected', handleMobileAuth);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('mobile-auth-injected', handleMobileAuth);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  const fetchCurrentUser = async (token) => {
    try {
      const response = await fetch(`${API_URL}/api/auth/me`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        setUser(data);
        // Route based on role
        if (data.role === 'platform_owner') {
          setCurrentView('platform-dashboard');
        } else if (data.role === 'admin') {
          setCurrentView('admin-dashboard');
        } else {
          setCurrentView('worker-home');
        }
      } else {
        localStorage.removeItem('token');
      }
    } catch (error) {
      console.error('Error fetching user:', error);
      localStorage.removeItem('token');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    setUser(null);
    setCurrentView('login');
  };

  if (loading) {
    return (
      <div className="loading-screen">
        <div className="spinner"></div>
        <p>جاري التحميل...</p>
      </div>
    );
  }

  return (
    <div className="app" dir="rtl">
      {!user ? (
        <LoginScreen 
          setUser={setUser} 
          setCurrentView={setCurrentView}
        />
      ) : user.role === 'platform_owner' ? (
        <PlatformOwnerDashboard 
          user={user} 
          onLogout={handleLogout} 
          currentView={currentView} 
          setCurrentView={setCurrentView}
          onShowPrivacyPolicy={() => setShowPrivacyPolicy(true)}
        />
      ) : user.role === 'admin' ? (
        <AdminDashboard 
          user={user} 
          onLogout={handleLogout} 
          currentView={currentView} 
          setCurrentView={setCurrentView}
          onShowPrivacyPolicy={() => setShowPrivacyPolicy(true)}
        />
      ) : (
        <WorkerDashboard user={user} onLogout={handleLogout} />
      )}
      
      {showPrivacyPolicy && (
        <PrivacyPolicy 
          user={user}
          onClose={() => setShowPrivacyPolicy(false)}
        />
      )}
    </div>
  );
}

// Login Screen
function LoginScreen({ setUser, setCurrentView }) {
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showRegisterModal, setShowRegisterModal] = useState(false);
  const [registrationEnabled, setRegistrationEnabled] = useState(false);

  useEffect(() => {
    fetchRegistrationSettings();
  }, []);

  const fetchRegistrationSettings = async () => {
    try {
      const response = await fetch(`${API_URL}/api/registration-settings`);
      const data = await response.json();
      setRegistrationEnabled(data.registration_enabled);
    } catch (error) {
      console.error('Error fetching registration settings:', error);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code })
      });

      const data = await response.json();

      if (response.ok) {
        saveTokenPersistently(data.token);
        setUser(data.user);
        if (data.user.role === 'platform_owner') {
          setCurrentView('platform-dashboard');
        } else if (data.user.role === 'admin') {
          setCurrentView('admin-dashboard');
        } else {
          setCurrentView('worker-home');
        }
      } else {
        setError(data.detail || 'كود غير صحيح');
      }
    } catch (error) {
      setError('حدث خطأ في الاتصال');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-container">
      <div className="auth-card">
        <img src="/logo.png" alt="Logo" className="auth-logo" />
        <h1 className="app-title">אבן אמין</h1>
        <p className="app-subtitle">نظام إدارة العمال</p>
        
        <form onSubmit={handleLogin} className="auth-form">
          <div className="form-group">
            <label>أدخل كود الدخول</label>
            <input
              type="text"
              value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase())}
              placeholder="مثال: ABC123"
              required
              autoFocus
            />
          </div>

          {error && <div className="error-message">{error}</div>}

          <button type="submit" className="btn btn-primary" disabled={loading}>
            {loading ? 'جاري الدخول...' : 'دخول'}
          </button>
        </form>

        {registrationEnabled && (
          <div style={{ marginTop: '20px' }}>
            <button 
              onClick={() => setShowRegisterModal(true)}
              className="btn btn-outline"
              style={{
                width: '100%',
                background: 'transparent',
                border: '2px solid #2563eb',
                color: '#2563eb',
                padding: '10px 20px',
                fontSize: '16px',
                fontWeight: '600'
              }}
            >
              ➕ إنشاء نظام جديد
            </button>
          </div>
        )}

        <div className="auth-footer">
          <div className="help-text">
            💡 استخدم الكود المقدم لك من المدير للدخول
          </div>
        </div>
      </div>

      {showRegisterModal && (
        <RegistrationModal 
          onClose={() => setShowRegisterModal(false)}
          setUser={setUser}
          setCurrentView={setCurrentView}
        />
      )}
    </div>
  );
}

// Registration Modal
function RegistrationModal({ onClose, setUser, setCurrentView }) {
  const [organizationName, setOrganizationName] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [generatedCode, setGeneratedCode] = useState('');
  const [autoLoggingIn, setAutoLoggingIn] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ organization_name: organizationName })
      });

      const data = await response.json();

      if (response.ok) {
        setGeneratedCode(data.code);
        // Auto-login immediately without showing success modal first
        await autoLogin(data.code, organizationName);
      } else {
        setError(data.detail || 'حدث خطأ في إنشاء النظام');
      }
    } catch (error) {
      setError('حدث خطأ في الاتصال');
    } finally {
      setLoading(false);
    }
  };

  const autoLogin = async (code, orgName) => {
    setAutoLoggingIn(true);
    try {
      const response = await fetch(`${API_URL}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code })
      });

      const data = await response.json();

      if (response.ok) {
        saveTokenPersistently(data.token);
        // Store the generated code temporarily to show in dashboard
        localStorage.setItem('new_account_code', JSON.stringify({
          code: code,
          organization_name: orgName
        }));
        setUser(data.user);
        if (data.user.role === 'admin') {
          setCurrentView('admin-dashboard');
        }
        // Close modal immediately and redirect
        onClose();
      } else {
        // If auto-login fails, show the success modal so user can copy code
        setSuccess(true);
      }
    } catch (error) {
      console.error('Auto-login error:', error);
      // If auto-login fails, show the success modal
      setSuccess(true);
    } finally {
      setAutoLoggingIn(false);
    }
  };

  const copyToClipboard = () => {
    // Fallback method that works without Clipboard API permissions
    const textArea = document.createElement('textarea');
    textArea.value = generatedCode;
    textArea.style.position = 'fixed';
    textArea.style.left = '-999999px';
    textArea.style.top = '-999999px';
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();
    
    try {
      document.execCommand('copy');
      alert('تم نسخ الكود! ✅');
    } catch (err) {
      console.error('Failed to copy:', err);
      // If all else fails, show the code in a prompt so user can copy manually
      prompt('انسخ هذا الكود:', generatedCode);
    } finally {
      document.body.removeChild(textArea);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        {!success && !autoLoggingIn ? (
          <>
            <h2>إنشاء نظام جديد</h2>
            <p style={{ marginBottom: '20px', color: '#666' }}>
              أدخل اسم منشأتك أو عملك لإنشاء نظام إدارة جديد
            </p>
            
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>اسم المنشأة / العمل *</label>
                <input
                  type="text"
                  value={organizationName}
                  onChange={(e) => setOrganizationName(e.target.value)}
                  placeholder="مثال: شركة البناء المتحدة"
                  required
                  autoFocus
                />
              </div>

              {error && <div className="error-message">{error}</div>}

              <div className="modal-actions">
                <button type="button" onClick={onClose} className="btn btn-secondary">
                  إلغاء
                </button>
                <button type="submit" className="btn btn-primary" disabled={loading}>
                  {loading ? 'جاري الإنشاء...' : 'إنشاء النظام'}
                </button>
              </div>
            </form>
          </>
        ) : autoLoggingIn ? (
          <div style={{ textAlign: 'center', padding: '40px 20px' }}>
            <div className="spinner" style={{ margin: '0 auto 20px', width: '50px', height: '50px' }}></div>
            <h2 style={{ marginBottom: '10px' }}>جاري تسجيل الدخول...</h2>
            <p style={{ color: '#666' }}>سيتم نقلك إلى لوحة التحكم</p>
          </div>
        ) : (
          <>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '48px', marginBottom: '20px' }}>✅</div>
              <h2>تم إنشاء النظام بنجاح!</h2>
              <p style={{ margin: '20px 0', color: '#666' }}>
                المنشأة: <strong>{organizationName}</strong>
              </p>
              
              <div style={{
                background: '#f0f9ff',
                border: '2px solid #2563eb',
                borderRadius: '8px',
                padding: '20px',
                margin: '20px 0'
              }}>
                <p style={{ marginBottom: '10px', fontWeight: 'bold' }}>كود الدخول الخاص بك:</p>
                <div style={{
                  background: 'white',
                  padding: '15px',
                  borderRadius: '4px',
                  fontSize: '24px',
                  fontWeight: 'bold',
                  color: '#2563eb',
                  letterSpacing: '2px',
                  marginBottom: '10px'
                }}>
                  {generatedCode}
                </div>
                <button 
                  onClick={copyToClipboard}
                  className="btn btn-secondary"
                  style={{ width: '100%' }}
                >
                  📋 نسخ الكود
                </button>
              </div>

              <div style={{
                background: '#fef3c7',
                border: '2px solid #f59e0b',
                borderRadius: '8px',
                padding: '15px',
                marginBottom: '20px',
                textAlign: 'right'
              }}>
                <p style={{ fontWeight: 'bold', marginBottom: '5px' }}>⚠️ مهم جداً:</p>
                <p style={{ fontSize: '14px' }}>
                  احفظ هذا الكود في مكان آمن. ستحتاجه لتسجيل الدخول إلى نظامك.
                </p>
              </div>

              {autoLoggingIn ? (
                <div style={{
                  background: '#d1fae5',
                  border: '2px solid #10b981',
                  borderRadius: '8px',
                  padding: '15px',
                  marginBottom: '20px',
                  textAlign: 'center'
                }}>
                  <div className="spinner" style={{ margin: '0 auto 10px', width: '30px', height: '30px' }}></div>
                  <p style={{ fontWeight: 'bold', color: '#059669' }}>
                    🔄 جاري تسجيل الدخول تلقائياً...
                  </p>
                </div>
              ) : (
                <button onClick={onClose} className="btn btn-primary" style={{ width: '100%' }}>
                  الدخول إلى لوحة التحكم ←
                </button>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// Platform Owner Dashboard
function PlatformOwnerDashboard({ user, onLogout, currentView, setCurrentView, onShowPrivacyPolicy }) {
  const [admins, setAdmins] = useState([]);
  const [stats, setStats] = useState({ total_systems: 0, total_workers: 0 });
  const [loading, setLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [registrationEnabled, setRegistrationEnabled] = useState(false);
  const [savingSettings, setSavingSettings] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [adminToDelete, setAdminToDelete] = useState(null);

  useEffect(() => {
    fetchAdmins();
    fetchRegistrationSettings();
  }, []);

  const fetchRegistrationSettings = async () => {
    try {
      const response = await fetch(`${API_URL}/api/registration-settings`);
      const data = await response.json();
      setRegistrationEnabled(data.registration_enabled);
    } catch (error) {
      console.error('Error fetching registration settings:', error);
    }
  };

  const handleToggleRegistration = async () => {
    const token = localStorage.getItem('token');
    setSavingSettings(true);
    try {
      const response = await fetch(`${API_URL}/api/registration-settings`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ registration_enabled: !registrationEnabled })
      });
      
      if (response.ok) {
        setRegistrationEnabled(!registrationEnabled);
      }
    } catch (error) {
      console.error('Error updating registration settings:', error);
    } finally {
      setSavingSettings(false);
    }
  };

  const fetchAdmins = async () => {
    const token = localStorage.getItem('token');
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/system-admins`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      setAdmins(data.admins || []);
      
      const totalWorkers = data.admins.reduce((sum, admin) => sum + admin.worker_count, 0);
      setStats({
        total_systems: data.admins.length,
        total_workers: totalWorkers
      });
    } catch (error) {
      console.error('Error fetching admins:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAdmin = async (adminId, orgName) => {
    const displayName = orgName || 'هذا النظام';
    console.log('Delete clicked for:', adminId, displayName);
    
    // Show custom confirmation modal instead of window.confirm
    setAdminToDelete({ id: adminId, name: displayName });
    setShowDeleteConfirm(true);
  };

  const confirmDelete = async () => {
    if (!adminToDelete) return;

    const token = localStorage.getItem('token');
    console.log('Proceeding with deletion...');
    
    try {
      const response = await fetch(`${API_URL}/api/system-admins/${adminToDelete.id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      console.log('Delete response status:', response.status);
      
      if (response.ok) {
        // Refresh the list after successful deletion
        console.log('Refreshing admin list...');
        await fetchAdmins();
        // Show success message
        setAdminToDelete({ ...adminToDelete, success: true });
      } else {
        const data = await response.json();
        setAdminToDelete({ ...adminToDelete, error: data.detail || 'خطأ غير معروف' });
      }
    } catch (error) {
      console.error('Error deleting admin:', error);
      setAdminToDelete({ ...adminToDelete, error: 'حدث خطأ في الاتصال' });
    }
  };

  const closeDeleteModal = () => {
    setShowDeleteConfirm(false);
    setAdminToDelete(null);
  };

  return (
    <div className="dashboard platform-dashboard">
      <header className="dashboard-header platform-header-bg">
        <div>
          <h1>🏛️ لوحة مدير المنصة</h1>
          <p className="subtitle">مرحباً، {user.name}</p>
        </div>
        <div style={{ display: 'flex', gap: '10px' }}>
          <button onClick={onShowPrivacyPolicy} className="btn btn-secondary" style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
            <svg width="16" height="16" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
            </svg>
            سياسة الخصوصية
          </button>
          <button onClick={onLogout} className="btn btn-secondary">تسجيل الخروج</button>
        </div>
      </header>

      <div className="dashboard-content">{showCreateForm ? (
          <CreateSystemAdminForm 
            onBack={() => setShowCreateForm(false)} 
            onSuccess={() => { setShowCreateForm(false); fetchAdmins(); }} 
          />
        ) : (
          <>
            <div className="platform-header">
              <h2>إدارة الأنظمة</h2>
              <button onClick={() => setShowCreateForm(true)} className="btn btn-primary">
                ➕ إنشاء نظام جديد
              </button>
            </div>

            <div className="stats-grid">
              <div className="stat-card stat-card-primary">
                <div className="stat-value">{stats.total_systems}</div>
                <div className="stat-label">🏢 إجمالي الأنظمة</div>
              </div>
              <div className="stat-card stat-card-success">
                <div className="stat-value">{stats.total_workers}</div>
                <div className="stat-label">👷 إجمالي العمال</div>
              </div>
            </div>

            <div className="registration-settings-card">
              <div className="settings-header">
                <div>
                  <h3>🔐 التحكم بالتسجيل العام</h3>
                  <p className="settings-description">
                    {registrationEnabled 
                      ? '✅ التسجيل مفعّل - يمكن للمستخدمين إنشاء أنظمة جديدة من صفحة الدخول'
                      : '🔒 التسجيل معطّل - لا يمكن إنشاء أنظمة جديدة من صفحة الدخول'}
                  </p>
                </div>
                <button 
                  onClick={handleToggleRegistration}
                  disabled={savingSettings}
                  className={`toggle-btn ${registrationEnabled ? 'active' : ''}`}
                >
                  <span className="toggle-slider"></span>
                </button>
              </div>
            </div>

            <div className="systems-section">
              <h3>الأنظمة المسجلة</h3>
              {loading ? (
                <div className="loading-screen"><div className="spinner"></div></div>
              ) : admins.length === 0 ? (
                <div className="empty-state">
                  <div className="empty-icon">📋</div>
                  <p>لا توجد أنظمة مسجلة</p>
                  <button onClick={() => setShowCreateForm(true)} className="btn btn-primary">
                    إنشاء نظام جديد
                  </button>
                </div>
              ) : (
                <div className="systems-grid">
                  {admins.map(admin => (
                    <div key={admin.id} className="system-card">
                      <div className="system-header">
                        <div>
                          <h4>{admin.organization_name}</h4>
                          <p className="system-admin">المدير: {admin.name}</p>
                        </div>
                        <button 
                          onClick={() => handleDeleteAdmin(admin.id, admin.organization_name)} 
                          className="btn btn-danger btn-sm"
                          title="حذف النظام"
                        >
                          🗑️
                        </button>
                      </div>
                      <div className="system-stats">
                        <div className="system-stat">
                          <span className="system-stat-value">{admin.worker_count}</span>
                          <span className="system-stat-label">عمال</span>
                        </div>
                      </div>
                      <div className="system-footer">
                        <span className="system-date">📅 {new Date(admin.created_at).toLocaleDateString('ar-EG')}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        )}
      </div>

      {showDeleteConfirm && adminToDelete && (
        <DeleteConfirmModal 
          systemName={adminToDelete.name}
          onConfirm={confirmDelete}
          onCancel={closeDeleteModal}
          success={adminToDelete.success}
          error={adminToDelete.error}
        />
      )}

    </div>
  );
}


// Delete Confirmation Modal
function DeleteConfirmModal({ systemName, onConfirm, onCancel, success, error }) {
  if (success) {
    return (
      <div className="modal-overlay" onClick={onCancel}>
        <div className="modal-content" onClick={(e) => e.stopPropagation()}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '48px', marginBottom: '20px' }}>✅</div>
            <h2>تم الحذف بنجاح</h2>
            <p style={{ margin: '20px 0', color: '#666' }}>
              تم حذف النظام "{systemName}" بنجاح
            </p>
            <button onClick={onCancel} className="btn btn-primary" style={{ width: '100%' }}>
              حسناً
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="modal-overlay" onClick={onCancel}>
        <div className="modal-content" onClick={(e) => e.stopPropagation()}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '48px', marginBottom: '20px' }}>❌</div>
            <h2>فشل الحذف</h2>
            <p style={{ margin: '20px 0', color: '#dc2626' }}>
              {error}
            </p>
            <button onClick={onCancel} className="btn btn-primary" style={{ width: '100%' }}>
              حسناً
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="modal-overlay" onClick={onCancel}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '48px', marginBottom: '20px' }}>⚠️</div>
          <h2>تأكيد الحذف</h2>
          <p style={{ margin: '20px 0', color: '#666', fontSize: '16px' }}>
            هل أنت متأكد من حذف النظام:
          </p>
          <p style={{ fontSize: '20px', fontWeight: 'bold', color: '#dc2626', marginBottom: '20px' }}>
            "{systemName}"
          </p>
          <div style={{
            background: '#fef3c7',
            border: '2px solid #f59e0b',
            borderRadius: '8px',
            padding: '15px',
            marginBottom: '20px',
            textAlign: 'right'
          }}>
            <p style={{ fontWeight: 'bold', marginBottom: '5px' }}>⚠️ تحذير:</p>
            <p style={{ fontSize: '14px' }}>
              سيتم حذف جميع البيانات المرتبطة بهذا النظام بشكل نهائي ولا يمكن التراجع عن هذا الإجراء.
            </p>
          </div>
          <div style={{ display: 'flex', gap: '10px' }}>
            <button onClick={onCancel} className="btn btn-secondary" style={{ flex: 1 }}>
              إلغاء
            </button>
            <button onClick={onConfirm} className="btn btn-danger" style={{ flex: 1 }}>
              تأكيد الحذف
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// Create System Admin Form
function CreateSystemAdminForm({ onBack, onSuccess }) {
  const [name, setName] = useState('');
  const [organizationName, setOrganizationName] = useState('');
  const [generatedCode, setGeneratedCode] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    const token = localStorage.getItem('token');
    try {
      const response = await fetch(`${API_URL}/api/system-admins`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          name,
          organization_name: organizationName
        })
      });

      const data = await response.json();

      if (response.ok) {
        setGeneratedCode(data.code);
      } else {
        setError(data.detail || 'حدث خطأ');
      }
    } catch (error) {
      setError('حدث خطأ في الاتصال');
    } finally {
      setLoading(false);
    }
  };

  if (generatedCode) {
    return (
      <div className="form-container">
        <div className="success-card">
          <div className="success-icon">✅</div>
          <h2>تم إنشاء النظام بنجاح!</h2>
          <div className="code-display">
            <p>كود الدخول للمدير:</p>
            <div className="generated-code">{generatedCode}</div>
          </div>
          <div className="warning-message">
            <strong>⚠️ مهم جداً:</strong><br/>
            احفظ هذا الكود في مكان آمن وشاركه مع المدير.<br/>
            لن تتمكن من رؤيته مرة أخرى!
          </div>
          <button onClick={onSuccess} className="btn btn-primary btn-large">
            العودة إلى لوحة التحكم
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="form-container">
      <button onClick={onBack} className="btn btn-secondary mb-2">↩️ رجوع</button>
      
      <div className="form-card">
        <h2>إنشاء نظام إدارة جديد</h2>
        <p className="form-subtitle">أنشئ نظاماً منفصلاً لمؤسسة جديدة</p>
        
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>اسم المؤسسة *</label>
            <input
              type="text"
              value={organizationName}
              onChange={(e) => setOrganizationName(e.target.value)}
              placeholder="مثال: شركة البناء العصري"
              required
              autoFocus
            />
          </div>

          <div className="form-group">
            <label>اسم المدير *</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="محمد أحمد"
              required
            />
          </div>

          {error && <div className="error-message">{error}</div>}

          <button type="submit" className="btn btn-primary btn-large" disabled={loading}>
            {loading ? 'جاري الإنشاء...' : '✨ إنشاء النظام'}
          </button>
        </form>
      </div>
    </div>
  );
}

// Worker Dashboard
function WorkerDashboard({ user, onLogout }) {
  const [stats, setStats] = useState(null);
  const [workdays, setWorkdays] = useState([]);
  const [payments, setPayments] = useState([]);
  const [worker, setWorker] = useState(null);
  const [activeTab, setActiveTab] = useState('workdays');
  const [loading, setLoading] = useState(true);
  const [dateFilter, setDateFilter] = useState({ start: '', end: '' });
  const [showDateFilter, setShowDateFilter] = useState(false);

  useEffect(() => {
    // Set default filter to current month
    const today = new Date();
    const start = formatLocalDate(new Date(today.getFullYear(), today.getMonth(), 1));
    const end = formatLocalDate(new Date(today.getFullYear(), today.getMonth() + 1, 0));
    
    setDateFilter({ start, end });
    fetchWorkerData(start, end);
  }, []);

  const fetchWorkerData = async (startDate = '', endDate = '') => {
    const token = localStorage.getItem('token');
    try {
      let workdaysUrl = `${API_URL}/api/workdays?worker_id=${user.worker_id}`;
      let paymentsUrl = `${API_URL}/api/payments?worker_id=${user.worker_id}`;
      
      if (startDate && endDate) {
        workdaysUrl += `&start_date=${startDate}&end_date=${endDate}`;
        paymentsUrl += `&start_date=${startDate}&end_date=${endDate}`;
      } else if (startDate) {
        workdaysUrl += `&start_date=${startDate}`;
        paymentsUrl += `&start_date=${startDate}`;
      } else if (endDate) {
        workdaysUrl += `&end_date=${endDate}`;
        paymentsUrl += `&end_date=${endDate}`;
      }

      const [statsRes, workdaysRes, paymentsRes, workerRes] = await Promise.all([
        fetch(`${API_URL}/api/analytics/worker/${user.worker_id}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(workdaysUrl, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(paymentsUrl, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/workers/${user.worker_id}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);

      const statsData = await statsRes.json();
      const workdaysData = await workdaysRes.json();
      const paymentsData = await paymentsRes.json();
      const workerData = await workerRes.json();

      setStats(statsData);
      setWorkdays(workdaysData.workdays || []);
      setPayments(paymentsData.payments || []);
      setWorker(workerData);
    } catch (error) {
      console.error('Error fetching worker data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleApplyFilter = (startDate, endDate) => {
    setDateFilter({ start: startDate, end: endDate });
    setShowDateFilter(false);
    fetchWorkerData(startDate, endDate);
  };

  const handleClearFilter = () => {
    setDateFilter({ start: '', end: '' });
    fetchWorkerData();
  };

  const handleAcceptPayment = async (paymentId) => {
    const token = localStorage.getItem('token');
    try {
      const response = await fetch(`${API_URL}/api/payments/${paymentId}/accept`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        fetchWorkerData();
      }
    } catch (error) {
      console.error('Error accepting payment:', error);
    }
  };

  if (loading) {
    return <div className="loading-screen"><div className="spinner"></div></div>;
  }

  const pendingPayments = payments.filter(p => p.status === 'pending');
  const isFiltered = dateFilter.start || dateFilter.end;

  return (
    <div className="dashboard">
      <header className="dashboard-header">
        <div className="header-content">
          <div className="header-logo-container">
            <img src="/logo.png" alt="Logo" className="header-logo" />
            <span className="header-logo-text">אבן אמין</span>
          </div>
          <div>
            <h1>مرحباً، {user.name}</h1>
            <p className="subtitle">لوحة العامل</p>
          </div>
        </div>
        <button onClick={onLogout} className="btn btn-secondary">تسجيل الخروج</button>
      </header>

      <div className="worker-dashboard-content">
        {pendingPayments.length > 0 && (
          <div className="alert alert-warning">
            لديك {pendingPayments.length} دفعة تحتاج إلى قبول
          </div>
        )}

        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-value">{stats?.workdays?.total_days || 0}</div>
            <div className="stat-label">إجمالي أيام العمل</div>
          </div>
          <div className="stat-card">
            <div className="stat-value">₪{stats?.workdays?.total_earned?.toFixed(2) || '0.00'}</div>
            <div className="stat-label">إجمالي المستحقات</div>
          </div>
          <div className="stat-card">
            <div className="stat-value">₪{stats?.payments?.total_paid?.toFixed(2) || '0.00'}</div>
            <div className="stat-label">المدفوعات المقبولة</div>
          </div>
          <div className="stat-card">
            <div className="stat-value">₪{stats?.payments?.balance_due?.toFixed(2) || '0.00'}</div>
            <div className="stat-label">الرصيد المتبقي</div>
          </div>
        </div>

        {/* Worker Info Widget - Compact Version */}
        <div className="info-card-compact" style={{ marginBottom: '1.5rem' }}>
          <h3>معلوماتي</h3>
          <div className="info-row">
            <span className="info-label">الاسم:</span>
            <span className="info-value">{worker?.name}</span>
          </div>
          {worker?.age && (
            <div className="info-row">
              <span className="info-label">العمر:</span>
              <span className="info-value">{worker.age}</span>
            </div>
          )}
          {worker?.national_id && (
            <div className="info-row">
              <span className="info-label">رقم الهوية:</span>
              <span className="info-value">{worker.national_id}</span>
            </div>
          )}
          <div className="info-row">
            <span className="info-label">الأجر اليومي:</span>
            <span className="info-value">₪{worker?.daily_wage?.toFixed(2)}</span>
          </div>
        </div>

        {/* Date Filter - Above Tabs */}
        <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem', alignItems: 'center' }}>
          <button 
            className={`btn ${isFiltered ? 'btn-filter-active' : 'btn-secondary'}`}
            onClick={() => setShowDateFilter(true)}
          >
            📅 تصفية حسب التاريخ
          </button>
          {isFiltered && (
            <button 
              className="btn btn-outline"
              onClick={handleClearFilter}
            >
              ✕ إزالة التصفية
            </button>
          )}
        </div>

        {/* Active Filter Banner */}
        {isFiltered && (
          <div className="filter-status-banner" style={{ marginBottom: '1rem' }}>
            <div className="filter-status-content">
              <span className="filter-status-icon">📅</span>
              <span className="filter-status-text">
                عرض البيانات من {dateFilter.start} إلى {dateFilter.end}
              </span>
            </div>
          </div>
        )}

        <div className="tabs">
          <button 
            className={`tab ${activeTab === 'workdays' ? 'active' : ''}`}
            onClick={() => setActiveTab('workdays')}
          >
            أيام العمل
          </button>
          <button 
            className={`tab ${activeTab === 'payments' ? 'active' : ''}`}
            onClick={() => setActiveTab('payments')}
          >
            المدفوعات {pendingPayments.length > 0 && <span className="badge">{pendingPayments.length}</span>}
          </button>
        </div>

        <div className="tab-content">
          {activeTab === 'workdays' && (
            <div className="workdays-list">
              {workdays.length === 0 ? (
                <div className="empty-state">لا توجد أيام عمل مسجلة</div>
              ) : (
                workdays.sort((a, b) => b.date.localeCompare(a.date)).map(workday => (
                  <div key={workday.id} className="workday-item">
                    <div className="workday-date">{workday.date}</div>
                    <div className="workday-status">
                      <span className={`status-badge status-${workday.status}`}>
                        {workday.status === 'full' ? 'يوم كامل' : workday.status === 'half' ? 'نصف يوم' : 'لم يعمل'}
                      </span>
                    </div>
                    <div className="workday-amount">₪{workday.earned_amount.toFixed(2)}</div>
                  </div>
                ))
              )}
            </div>
          )}

          {activeTab === 'payments' && (
            <div className="payments-list">
              {payments.length === 0 ? (
                <div className="empty-state">لا توجد مدفوعات</div>
              ) : (
                payments.sort((a, b) => b.date.localeCompare(a.date)).map(payment => (
                  <div key={payment.id} className={`payment-item ${payment.status === 'pending' ? 'pending' : ''}`}>
                    <div className="payment-header">
                      <div>
                        <div className="payment-amount">₪{payment.amount.toFixed(2)}</div>
                        <div className="payment-type">{payment.type === 'deposit' ? 'دفعة مقدمة' : payment.type === 'salary' ? 'راتب' : 'دفعة'}</div>
                      </div>
                      <div className="payment-date">{payment.date}</div>
                    </div>
                    {payment.notes && <div className="payment-notes">{payment.notes}</div>}
                    <div className="payment-footer">
                      {payment.status === 'pending' ? (
                        <button 
                          onClick={() => handleAcceptPayment(payment.id)}
                          className="btn btn-success btn-sm"
                        >
                          قبول الدفعة
                        </button>
                      ) : (
                        <span className="status-badge status-accepted">تم القبول</span>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
          )}
        </div>

        {/* Date Filter Modal */}
        {showDateFilter && (
          <DateFilterModal 
            onClose={() => setShowDateFilter(false)}
            onApply={handleApplyFilter}
            currentStart={dateFilter.start}
            currentEnd={dateFilter.end}
          />
        )}
      </div>
    </div>
  );
}

// Welcome Modal for New Accounts
function WelcomeModal({ code, organizationName, onClose }) {
  const copyToClipboard = () => {
    const textArea = document.createElement('textarea');
    textArea.value = code;
    textArea.style.position = 'fixed';
    textArea.style.left = '-999999px';
    textArea.style.top = '-999999px';
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();
    
    try {
      document.execCommand('copy');
      alert('تم نسخ الكود! ✅');
    } catch (err) {
      console.error('Failed to copy:', err);
      prompt('انسخ هذا الكود:', code);
    } finally {
      document.body.removeChild(textArea);
    }
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '48px', marginBottom: '20px' }}>🎉</div>
          <h2>مرحباً بك في אבן אמין!</h2>
          <p style={{ margin: '20px 0', color: '#666', fontSize: '18px' }}>
            تم إنشاء نظامك بنجاح
          </p>
          <p style={{ margin: '10px 0 30px 0' }}>
            المنشأة: <strong style={{ fontSize: '20px', color: '#2563eb' }}>{organizationName}</strong>
          </p>
          
          <div style={{
            background: '#f0f9ff',
            border: '2px solid #2563eb',
            borderRadius: '8px',
            padding: '20px',
            margin: '20px 0'
          }}>
            <p style={{ marginBottom: '10px', fontWeight: 'bold', fontSize: '16px' }}>كود الدخول الخاص بك:</p>
            <div style={{
              background: 'white',
              padding: '15px',
              borderRadius: '4px',
              fontSize: '28px',
              fontWeight: 'bold',
              color: '#2563eb',
              letterSpacing: '3px',
              marginBottom: '15px'
            }}>
              {code}
            </div>
            <button 
              onClick={copyToClipboard}
              className="btn btn-secondary"
              style={{ width: '100%', fontSize: '16px' }}
            >
              📋 نسخ الكود
            </button>
          </div>

          <div style={{
            background: '#fef3c7',
            border: '2px solid #f59e0b',
            borderRadius: '8px',
            padding: '15px',
            marginBottom: '20px',
            textAlign: 'right'
          }}>
            <p style={{ fontWeight: 'bold', marginBottom: '5px' }}>⚠️ مهم جداً:</p>
            <p style={{ fontSize: '14px' }}>
              احفظ هذا الكود في مكان آمن. ستحتاجه لتسجيل الدخول إلى نظامك في المستقبل.
            </p>
          </div>

          <div style={{
            background: '#d1fae5',
            border: '2px solid #10b981',
            borderRadius: '8px',
            padding: '15px',
            marginBottom: '20px',
            textAlign: 'right'
          }}>
            <p style={{ fontWeight: 'bold', marginBottom: '5px', color: '#059669' }}>✨ الخطوات التالية:</p>
            <ul style={{ textAlign: 'right', marginRight: '20px', fontSize: '14px', lineHeight: '1.8' }}>
              <li>أضف عمالك من قسم "العمال"</li>
              <li>أنشئ مشاريعك من قسم "المشاريع"</li>
              <li>تابع أيام العمل والمدفوعات</li>
            </ul>
          </div>

          <button onClick={onClose} className="btn btn-primary" style={{ width: '100%', fontSize: '18px', padding: '12px' }}>
            ابدأ الآن 🚀
          </button>
        </div>
      </div>
    </div>
  );
}

// Admin Dashboard
function AdminDashboard({ user, onLogout, currentView, setCurrentView, onShowPrivacyPolicy }) {
  const [dashboardStats, setDashboardStats] = useState(null);
  const [workers, setWorkers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [selectedWorker, setSelectedWorker] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dateFilter, setDateFilter] = useState({ start: '', end: '' });
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [workersFilter, setWorkersFilter] = useState({ date: '', dateStart: '', dateEnd: '', isRange: false });
  const [showWelcomeModal, setShowWelcomeModal] = useState(false);
  const [accountInfo, setAccountInfo] = useState(null);
  const [showDeleteAccountModal, setShowDeleteAccountModal] = useState(false);
  const [deleteAccountStatus, setDeleteAccountStatus] = useState(null);

  useEffect(() => {
    // Set default filter to current month
    const today = new Date();
    const start = formatLocalDate(new Date(today.getFullYear(), today.getMonth(), 1));
    const end = formatLocalDate(new Date(today.getFullYear(), today.getMonth() + 1, 0));
    
    setDateFilter({ start, end });
    fetchDashboardData(start, end);
    
    // Check if this is a newly created account
    const newAccountData = localStorage.getItem('new_account_code');
    if (newAccountData) {
      setAccountInfo(JSON.parse(newAccountData));
      setShowWelcomeModal(true);
      // Remove from localStorage after reading
      localStorage.removeItem('new_account_code');
    }
  }, []);

  const handleDeleteAccount = () => {
    setShowDeleteAccountModal(true);
  };

  const confirmDeleteAccount = async () => {
    const token = localStorage.getItem('token');
    try {
      const response = await fetch(`${API_URL}/api/auth/delete-account`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        setDeleteAccountStatus('success');
        // Logout after 2 seconds
        setTimeout(() => {
          localStorage.removeItem('token');
          window.location.reload();
        }, 2000);
      } else {
        const data = await response.json();
        setDeleteAccountStatus({ error: data.detail || 'فشل حذف الحساب' });
      }
    } catch (error) {
      console.error('Error deleting account:', error);
      setDeleteAccountStatus({ error: 'حدث خطأ في الاتصال' });
    }
  };

  const fetchDashboardData = async (startDate = '', endDate = '') => {
    const token = localStorage.getItem('token');
    setLoading(true);
    try {
      let statsUrl = `${API_URL}/api/analytics/dashboard`;
      if (startDate && endDate) {
        statsUrl += `?start_date=${startDate}&end_date=${endDate}`;
      } else if (startDate) {
        statsUrl += `?start_date=${startDate}`;
      } else if (endDate) {
        statsUrl += `?end_date=${endDate}`;
      }

      const [statsRes, workersRes, projectsRes] = await Promise.all([
        fetch(statsUrl, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/workers`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/projects`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);

      const statsData = await statsRes.json();
      const workersData = await workersRes.json();
      const projectsData = await projectsRes.json();

      setDashboardStats(statsData);
      setWorkers(workersData.workers || []);
      setProjects(projectsData.projects || []);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchFilteredWorkers = async (filterParams = {}) => {
    const token = localStorage.getItem('token');
    try {
      let workersUrl = `${API_URL}/api/workers`;
      const params = new URLSearchParams();
      
      console.log('🔍 Fetching workers with filter params:', filterParams);
      
      if (filterParams.date) {
        params.append('filter_date', filterParams.date);
      } else if (filterParams.dateStart && filterParams.dateEnd) {
        params.append('filter_date_start', filterParams.dateStart);
        params.append('filter_date_end', filterParams.dateEnd);
      }
      
      if (params.toString()) {
        workersUrl += `?${params.toString()}`;
      }

      console.log('🔍 API URL:', workersUrl);

      const workersRes = await fetch(workersUrl, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      const workersData = await workersRes.json();
      console.log('🔍 Received workers:', workersData.workers?.length || 0);
      setWorkers(workersData.workers || []);
    } catch (error) {
      console.error('Error fetching filtered workers:', error);
    }
  };

  // Smart refresh that respects active workers filter
  const refreshWorkers = () => {
    const hasActiveFilter = workersFilter.date || (workersFilter.dateStart && workersFilter.dateEnd);
    if (hasActiveFilter) {
      // Re-apply the active filter
      const filterParams = {};
      if (!workersFilter.isRange && workersFilter.date) {
        filterParams.date = workersFilter.date;
      } else if (workersFilter.isRange && workersFilter.dateStart && workersFilter.dateEnd) {
        filterParams.dateStart = workersFilter.dateStart;
        filterParams.dateEnd = workersFilter.dateEnd;
      }
      fetchFilteredWorkers(filterParams);
    } else {
      // No filter active, fetch all workers
      fetchDashboardData(dateFilter.start, dateFilter.end);
    }
  };

  const handleFilterChange = (start, end) => {
    setDateFilter({ start, end });
    fetchDashboardData(start, end);
  };

  const handleClearFilter = () => {
    setDateFilter({ start: '', end: '' });
    fetchDashboardData('', '');
  };

  const renderContent = () => {
    switch (currentView) {
      case 'admin-dashboard':
        return <DashboardView stats={dashboardStats} loading={loading} dateFilter={dateFilter} onFilterChange={handleFilterChange} onClearFilter={handleClearFilter} />;
      case 'workers':
        return <WorkersView 
          workers={workers} 
          onSelectWorker={(w) => { setSelectedWorker(w); setCurrentView('worker-detail'); }} 
          onRefresh={refreshWorkers} 
          onBulkAssign={() => setShowBulkModal(true)} 
          workersFilter={workersFilter}
          onWorkersFilterChange={setWorkersFilter}
          onFetchFilteredWorkers={fetchFilteredWorkers}
        />;
      case 'worker-detail':
        return <WorkerDetailView worker={selectedWorker} projects={projects} onBack={() => setCurrentView('workers')} onRefresh={refreshWorkers} />;
      case 'attendance-list':
        return <AttendanceListView workers={workers} dateFilter={dateFilter} onFilterChange={handleFilterChange} onClearFilter={handleClearFilter} />;
      case 'projects':
        return <ProjectsView projects={projects} onRefresh={() => fetchDashboardData(dateFilter.start, dateFilter.end)} />;
      default:
        return <DashboardView stats={dashboardStats} loading={loading} dateFilter={dateFilter} onFilterChange={handleFilterChange} onClearFilter={handleClearFilter} />;
    }
  };

  return (
    <div className="dashboard">
      <header className="dashboard-header">
        <div className="header-content">
          <div className="header-logo-container">
            <img src="/logo.png" alt="Logo" className="header-logo" />
            <span className="header-logo-text">אבן אמין</span>
          </div>
          <div>
            <h1>لوحة المدير</h1>
            <p className="subtitle">مرحباً، {user.name}</p>
            {user.organization_name && <p className="org-name">📍 {user.organization_name}</p>}
          </div>
        </div>
        <div style={{ display: 'flex', gap: '10px' }}>
          <button onClick={onShowPrivacyPolicy} className="btn btn-secondary" style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
            <svg width="16" height="16" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
            </svg>
            سياسة الخصوصية
          </button>
          {user.self_registered && (
            <button onClick={handleDeleteAccount} className="btn btn-danger" style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
              🗑️ حذف الحساب
            </button>
          )}
          <button onClick={onLogout} className="btn btn-secondary">تسجيل الخروج</button>
        </div>
      </header>

      <nav className="dashboard-nav">
        <button 
          className={currentView === 'admin-dashboard' ? 'active' : ''}
          onClick={() => setCurrentView('admin-dashboard')}
        >
          الرئيسية
        </button>
        <button 
          className={currentView === 'workers' || currentView === 'worker-detail' ? 'active' : ''}
          onClick={() => setCurrentView('workers')}
        >
          العمال
        </button>
        <button 
          className={currentView === 'attendance-list' ? 'active' : ''}
          onClick={() => setCurrentView('attendance-list')}
        >
          القائمة
        </button>
        <button 
          className={currentView === 'projects' ? 'active' : ''}
          onClick={() => setCurrentView('projects')}
        >
          المشاريع
        </button>
      </nav>

      <div className="dashboard-content">
        {renderContent()}
      </div>

      {showBulkModal && (
        <BulkMultiWorkerModal 
          workers={workers}
          projects={projects}
          onClose={() => setShowBulkModal(false)}
          onSuccess={() => { setShowBulkModal(false); refreshWorkers(); }}
        />
      )}

      {showWelcomeModal && accountInfo && (
        <WelcomeModal 
          code={accountInfo.code}
          organizationName={accountInfo.organization_name}
          onClose={() => setShowWelcomeModal(false)}
        />
      )}

      {showDeleteAccountModal && (
        <DeleteAccountModal 
          organizationName={user.organization_name}
          onConfirm={confirmDeleteAccount}
          onCancel={() => setShowDeleteAccountModal(false)}
          status={deleteAccountStatus}
        />
      )}
    </div>
  );
}

// Delete Account Modal
function DeleteAccountModal({ organizationName, onConfirm, onCancel, status }) {
  if (status === 'success') {
    return (
      <div className="modal-overlay">
        <div className="modal-content" onClick={(e) => e.stopPropagation()}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '48px', marginBottom: '20px' }}>✅</div>
            <h2>تم حذف الحساب بنجاح</h2>
            <p style={{ margin: '20px 0', color: '#666' }}>
              سيتم تسجيل خروجك الآن...
            </p>
            <div className="spinner" style={{ margin: '20px auto', width: '40px', height: '40px' }}></div>
          </div>
        </div>
      </div>
    );
  }

  if (status?.error) {
    return (
      <div className="modal-overlay" onClick={onCancel}>
        <div className="modal-content" onClick={(e) => e.stopPropagation()}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '48px', marginBottom: '20px' }}>❌</div>
            <h2>فشل حذف الحساب</h2>
            <p style={{ margin: '20px 0', color: '#dc2626' }}>
              {status.error}
            </p>
            <button onClick={onCancel} className="btn btn-primary" style={{ width: '100%' }}>
              حسناً
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="modal-overlay" onClick={onCancel}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '48px', marginBottom: '20px' }}>⚠️</div>
          <h2>حذف الحساب نهائياً</h2>
          <p style={{ margin: '20px 0', color: '#666', fontSize: '16px' }}>
            هل أنت متأكد من حذف حسابك:
          </p>
          <p style={{ fontSize: '20px', fontWeight: 'bold', color: '#dc2626', marginBottom: '20px' }}>
            {organizationName}
          </p>
          <div style={{
            background: '#fee2e2',
            border: '2px solid #dc2626',
            borderRadius: '8px',
            padding: '15px',
            marginBottom: '20px',
            textAlign: 'right'
          }}>
            <p style={{ fontWeight: 'bold', marginBottom: '10px', color: '#dc2626' }}>❌ تحذير شديد:</p>
            <ul style={{ textAlign: 'right', marginRight: '20px', fontSize: '14px', lineHeight: '1.8' }}>
              <li>سيتم حذف حسابك بشكل نهائي</li>
              <li>سيتم حذف جميع العمال والمشاريع</li>
              <li>سيتم حذف جميع سجلات أيام العمل</li>
              <li>سيتم حذف جميع المدفوعات</li>
              <li><strong>لا يمكن التراجع عن هذا الإجراء</strong></li>
            </ul>
          </div>
          <div style={{ display: 'flex', gap: '10px' }}>
            <button onClick={onCancel} className="btn btn-secondary" style={{ flex: 1 }}>
              إلغاء
            </button>
            <button onClick={onConfirm} className="btn btn-danger" style={{ flex: 1 }}>
              نعم، احذف حسابي نهائياً
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// Dashboard View
function DashboardView({ stats, loading, dateFilter, onFilterChange, onClearFilter }) {
  const [showFilterModal, setShowFilterModal] = useState(false);
  
  if (loading) {
    return <div className="loading-screen"><div className="spinner"></div></div>;
  }

  const isFiltered = (dateFilter?.start || dateFilter?.end) && (stats?.filtered_period?.start_date || stats?.filtered_period?.end_date);

  return (
    <div>
      <div className="view-header">
        <div className="filter-actions">
          <button onClick={() => setShowFilterModal(true)} className="btn btn-filter">
            📅 فلترة حسب التاريخ
            {isFiltered && <span className="filter-badge">✓</span>}
          </button>
          {isFiltered && (
            <button onClick={onClearFilter} className="btn btn-secondary btn-sm">
              ✕ إزالة
            </button>
          )}
        </div>
        <div></div>
      </div>

      {isFiltered && (
        <div className="filter-indicator">
          📊 عرض البيانات من {stats.filtered_period.start_date || 'البداية'} إلى {stats.filtered_period.end_date || 'الآن'}
        </div>
      )}

      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-value">{stats?.total_workers || 0}</div>
          <div className="stat-label">إجمالي العمال</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">{stats?.total_projects || 0}</div>
          <div className="stat-label">إجمالي المشاريع</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">
            {isFiltered 
              ? (stats?.filtered_period?.total_days ?? 0)
              : (stats?.this_month?.total_days ?? 0)
            }
          </div>
          <div className="stat-label">{isFiltered ? 'أيام العمل (الفترة المحددة)' : 'أيام العمل (كل الوقت)'}</div>
          <div className="stat-detail">
            كامل: {isFiltered 
              ? (stats?.filtered_period?.full_days ?? 0)
              : (stats?.this_month?.full_days ?? 0)
            } | 
            نصف: {isFiltered 
              ? (stats?.filtered_period?.half_days ?? 0)
              : (stats?.this_month?.half_days ?? 0)
            }
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-value">{stats?.pending_payments || 0}</div>
          <div className="stat-label">دفعات معلقة</div>
        </div>
      </div>

      <div className="financial-summary">
        <h3>الملخص المالي {isFiltered ? '(الفترة المحددة)' : '(كل الوقت)'}</h3>
        <div className="finance-grid">
          <div className="finance-item">
            <span className="finance-label">إجمالي المستحقات:</span>
            <span className="finance-value">₪{stats?.financials?.total_earned?.toFixed(2) || '0.00'}</span>
          </div>
          <div className="finance-item">
            <span className="finance-label">إجمالي المدفوع:</span>
            <span className="finance-value">₪{stats?.financials?.total_paid?.toFixed(2) || '0.00'}</span>
          </div>
          <div className="finance-item highlight">
            <span className="finance-label">الرصيد المتبقي:</span>
            <span className="finance-value">₪{stats?.financials?.balance_due?.toFixed(2) || '0.00'}</span>
          </div>
        </div>
      </div>

      {/* Additional Insights Widget */}
      <div className="insights-widget">
        <h3>📊 رؤى إضافية</h3>
        <div className="insights-grid">
          <div className="insight-card insight-financial">
            <div className="insight-icon">💰</div>
            <div className="insight-content">
              <div className="insight-value">₪{stats?.additional_metrics?.total_paid_this_month?.toFixed(2) || '0.00'}</div>
              <div className="insight-label">إجمالي المدفوع هذا الشهر</div>
            </div>
          </div>
          <div className="insight-card insight-financial">
            <div className="insight-icon">📈</div>
            <div className="insight-content">
              <div className="insight-value">₪{stats?.additional_metrics?.average_daily_wage?.toFixed(2) || '0.00'}</div>
              <div className="insight-label">متوسط الراتب اليومي</div>
            </div>
          </div>
          <div className="insight-card insight-worker">
            <div className="insight-icon">👥</div>
            <div className="insight-content">
              <div className="insight-value">{stats?.additional_metrics?.active_workers || 0}</div>
              <div className="insight-label">العمال النشطون</div>
              <div className="insight-note">(آخر 30 يوم)</div>
            </div>
          </div>
          <div className="insight-card insight-worker">
            <div className="insight-icon">📅</div>
            <div className="insight-content">
              <div className="insight-value">{stats?.additional_metrics?.attendance_rate?.toFixed(1) || '0.0'}%</div>
              <div className="insight-label">معدل الحضور</div>
              <div className="insight-note">(آخر 30 يوم)</div>
            </div>
          </div>
        </div>
      </div>

      {showFilterModal && (
        <DateFilterModal 
          onClose={() => setShowFilterModal(false)}
          onApply={(start, end) => {
            onFilterChange(start, end);
            setShowFilterModal(false);
          }}
          currentStart={dateFilter?.start}
          currentEnd={dateFilter?.end}
        />
      )}
    </div>
  );
}

// Workers View
function WorkersView({ workers, onSelectWorker, onRefresh, onBulkAssign, workersFilter, onWorkersFilterChange, onFetchFilteredWorkers }) {
  const [showAddModal, setShowAddModal] = useState(false);
  const [showFilterModal, setShowFilterModal] = useState(false);

  const handleApplyFilter = (filterData) => {
    // Use the filter data directly from the modal instead of relying on state
    const filterParams = {};
    if (!filterData.isRange && filterData.date) {
      filterParams.date = filterData.date;
    } else if (filterData.isRange && filterData.dateStart && filterData.dateEnd) {
      filterParams.dateStart = filterData.dateStart;
      filterParams.dateEnd = filterData.dateEnd;
    }
    onFetchFilteredWorkers(filterParams);
    setShowFilterModal(false);
  };

  const handleClearFilter = () => {
    onWorkersFilterChange({ date: '', dateStart: '', dateEnd: '', isRange: false });
    onFetchFilteredWorkers({});
  };

  const hasActiveFilter = workersFilter.date || (workersFilter.dateStart && workersFilter.dateEnd);

  return (
    <div>
      <div className="view-header-workers">
        <div className="header-actions-workers">
          <button onClick={() => setShowAddModal(true)} className="btn btn-primary btn-flexible">
            <span className="btn-icon">➕</span>
            إضافة عامل
          </button>
          <button onClick={onBulkAssign} className="btn btn-success btn-flexible">
            <span className="btn-icon">📅</span>
            تسجيل جماعي
          </button>
          <button 
            onClick={() => setShowFilterModal(true)} 
            className={`btn btn-flexible ${hasActiveFilter ? 'btn-filter-active' : 'btn-outline'}`}
          >
            <span className="btn-icon">🔍</span>
            {hasActiveFilter ? 'تصفية نشطة' : 'تصفية حسب أيام العمل'}
          </button>
          {hasActiveFilter && (
            <button onClick={handleClearFilter} className="btn btn-secondary btn-flexible">
              <span className="btn-icon">✕</span>
              إلغاء التصفية
            </button>
          )}
        </div>
      </div>

      {hasActiveFilter && (
        <div className="filter-status-banner">
          <div className="filter-status-icon">✓</div>
          <div className="filter-status-text">
            {workersFilter.isRange ? (
              <>عرض العمال الذين لم يوقعوا من {workersFilter.dateStart} إلى {workersFilter.dateEnd}</>
            ) : (
              <>عرض العمال الذين لم يوقعوا في {workersFilter.date}</>
            )}
          </div>
        </div>
      )}

      {workers.length === 0 ? (
        <div className="empty-state">
          {hasActiveFilter ? 'لا يوجد عمال بدون توقيع للفترة المحددة' : 'لا يوجد عمال. ابدأ بإضافة عامل جديد.'}
        </div>
      ) : (
        <div className="workers-grid">
          {workers.map(worker => (
            <div key={worker.id} className="worker-card" onClick={() => onSelectWorker(worker)}>
              <div className="worker-name">{worker.name}</div>
              <div className="worker-wage">₪{worker.daily_wage.toFixed(2)} / يوم</div>
              {worker.national_id && <div className="worker-id">هوية: {worker.national_id}</div>}
            </div>
          ))}
        </div>
      )}

      {showAddModal && (
        <AddWorkerModal 
          onClose={() => setShowAddModal(false)}
          onSuccess={() => { setShowAddModal(false); onRefresh(); }}
        />
      )}

      {showFilterModal && (
        <WorkersFilterModal 
          filter={workersFilter}
          onFilterChange={onWorkersFilterChange}
          onClose={() => setShowFilterModal(false)}
          onApply={handleApplyFilter}
        />
      )}
    </div>
  );
}

// Workers Filter Modal
function WorkersFilterModal({ filter, onFilterChange, onClose, onApply }) {
  const [isRange, setIsRange] = useState(filter.isRange || false);
  const [date, setDate] = useState(filter.date || '');
  const [dateStart, setDateStart] = useState(filter.dateStart || '');
  const [dateEnd, setDateEnd] = useState(filter.dateEnd || '');

  const handleApply = () => {
    const filterData = {
      date: !isRange ? date : '',
      dateStart: isRange ? dateStart : '',
      dateEnd: isRange ? dateEnd : '',
      isRange
    };
    onFilterChange(filterData);
    onApply(filterData);
  };

  const canApply = isRange ? (dateStart && dateEnd) : date;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal modal-filter" onClick={(e) => e.stopPropagation()}>
        <div className="filter-modal-header">
          <h2>🔍 تصفية العمال حسب أيام العمل</h2>
          <button onClick={onClose} className="close-btn-simple">✕</button>
        </div>

        <div className="filter-modal-body">
          <p className="filter-description">
            اختر تاريخًا أو فترة زمنية لعرض العمال الذين لم يوقعوا بعد
          </p>

          {/* Toggle between single date and range */}
          <div className="filter-type-toggle">
            <button 
              className={`toggle-btn ${!isRange ? 'active' : ''}`}
              onClick={() => setIsRange(false)}
            >
              📅 تاريخ واحد
            </button>
            <button 
              className={`toggle-btn ${isRange ? 'active' : ''}`}
              onClick={() => setIsRange(true)}
            >
              📆 فترة زمنية
            </button>
          </div>

          {/* Single date picker */}
          {!isRange && (
            <div className="filter-date-section">
              <label className="date-picker-box">
                <span className="date-label">📅 اختر التاريخ</span>
                <input
                  type="date"
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                  className="modern-date-input"
                />
              </label>
            </div>
          )}

          {/* Date range pickers */}
          {isRange && (
            <div className="filter-date-section">
              <div className="date-range-picker">
                <label className="date-picker-box">
                  <span className="date-label">🚀 من تاريخ</span>
                  <input
                    type="date"
                    value={dateStart}
                    onChange={(e) => setDateStart(e.target.value)}
                    className="modern-date-input"
                  />
                </label>
                <div className="date-arrow">←</div>
                <label className="date-picker-box">
                  <span className="date-label">🏁 إلى تاريخ</span>
                  <input
                    type="date"
                    value={dateEnd}
                    onChange={(e) => setDateEnd(e.target.value)}
                    className="modern-date-input"
                  />
                </label>
              </div>
            </div>
          )}

          {canApply && (
            <div className="filter-preview">
              <div className="preview-icon">✓</div>
              <div className="preview-text">
                <strong>سيتم عرض:</strong>
                <span>
                  {isRange 
                    ? `العمال الذين لم يوقعوا من ${dateStart} إلى ${dateEnd}`
                    : `العمال الذين لم يوقعوا في ${date}`
                  }
                </span>
              </div>
            </div>
          )}
        </div>

        <div className="filter-modal-footer">
          <button onClick={onClose} className="btn btn-outline">
            إلغاء
          </button>
          <button 
            onClick={handleApply} 
            className="btn btn-apply"
            disabled={!canApply}
          >
            ✓ تطبيق التصفية
          </button>
        </div>
      </div>
    </div>
  );
}

// Add Worker Modal
function AddWorkerModal({ onClose, onSuccess }) {
  const [formData, setFormData] = useState({
    name: '',
    age: '',
    national_id: '',
    daily_wage: '',
    contact: ''
  });
  const [generatedCode, setGeneratedCode] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    const token = localStorage.getItem('token');
    try {
      const response = await fetch(`${API_URL}/api/workers`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          name: formData.name,
          age: formData.age ? parseInt(formData.age) : null,
          national_id: formData.national_id || null,
          daily_wage: parseFloat(formData.daily_wage),
          contact: formData.contact || null
        })
      });

      const data = await response.json();

      if (response.ok) {
        setGeneratedCode(data.code);
      } else {
        setError(data.detail || 'حدث خطأ');
      }
    } catch (error) {
      setError('حدث خطأ في الاتصال');
    } finally {
      setLoading(false);
    }
  };

  if (generatedCode) {
    return (
      <div className="modal-overlay" onClick={onClose}>
        <div className="modal" onClick={(e) => e.stopPropagation()}>
          <div className="success-icon">✓</div>
          <h2>تم إضافة العامل بنجاح</h2>
          <p>كود الدخول للعامل:</p>
          <div className="generated-code">{generatedCode}</div>
          <div className="warning-message">
            <strong>مهم:</strong> شارك هذا الكود مع العامل بشكل آمن.
          </div>
          <button onClick={onSuccess} className="btn btn-primary">حسناً</button>
        </div>
      </div>
    );
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <h2>إضافة عامل جديد</h2>
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>الاسم *</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({...formData, name: e.target.value})}
              required
            />
          </div>
          <div className="form-group">
            <label>العمر</label>
            <input
              type="number"
              value={formData.age}
              onChange={(e) => setFormData({...formData, age: e.target.value})}
            />
          </div>
          <div className="form-group">
            <label>رقم الهوية</label>
            <input
              type="text"
              value={formData.national_id}
              onChange={(e) => setFormData({...formData, national_id: e.target.value})}
            />
          </div>
          <div className="form-group">
            <label>الأجر اليومي (₪) *</label>
            <input
              type="number"
              step="0.01"
              value={formData.daily_wage}
              onChange={(e) => setFormData({...formData, daily_wage: e.target.value})}
              required
            />
          </div>
          <div className="form-group">
            <label>معلومات الاتصال</label>
            <input
              type="text"
              value={formData.contact}
              onChange={(e) => setFormData({...formData, contact: e.target.value})}
              placeholder="رقم الهاتف أو البريد الإلكتروني"
            />
          </div>

          {error && <div className="error-message">{error}</div>}

          <div className="modal-actions">
            <button type="button" onClick={onClose} className="btn btn-secondary">إلغاء</button>
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? 'جاري الإضافة...' : 'إضافة'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Worker Detail View
function WorkerDetailView({ worker, projects, onBack, onRefresh }) {
  const [activeTab, setActiveTab] = useState('workdays');
  const [workdays, setWorkdays] = useState([]);
  const [payments, setPayments] = useState([]);
  const [stats, setStats] = useState(null);
  const [showAddWorkdayModal, setShowAddWorkdayModal] = useState(false);
  const [showEditWorkdayModal, setShowEditWorkdayModal] = useState(false);
  const [selectedWorkday, setSelectedWorkday] = useState(null);
  const [showAddPaymentModal, setShowAddPaymentModal] = useState(false);
  const [showEditPaymentModal, setShowEditPaymentModal] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [dateFilter, setDateFilter] = useState({ start: '', end: '' });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Set default filter to current month
    const today = new Date();
    const start = formatLocalDate(new Date(today.getFullYear(), today.getMonth(), 1));
    const end = formatLocalDate(new Date(today.getFullYear(), today.getMonth() + 1, 0));
    
    setDateFilter({ start, end });
    fetchWorkerDetails(start, end);
  }, [worker]);

  const fetchWorkerDetails = async (startDate = '', endDate = '') => {
    const token = localStorage.getItem('token');
    setLoading(true);
    try {
      let workdaysUrl = `${API_URL}/api/workdays?worker_id=${worker.id}`;
      let statsUrl = `${API_URL}/api/analytics/worker/${worker.id}`;
      let paymentsUrl = `${API_URL}/api/payments?worker_id=${worker.id}`;
      
      if (startDate && endDate) {
        workdaysUrl += `&start_date=${startDate}&end_date=${endDate}`;
        statsUrl += `?start_date=${startDate}&end_date=${endDate}`;
        paymentsUrl += `&start_date=${startDate}&end_date=${endDate}`;
      } else if (startDate) {
        workdaysUrl += `&start_date=${startDate}`;
        statsUrl += `?start_date=${startDate}`;
        paymentsUrl += `&start_date=${startDate}`;
      } else if (endDate) {
        workdaysUrl += `&end_date=${endDate}`;
        statsUrl += `?end_date=${endDate}`;
        paymentsUrl += `&end_date=${endDate}`;
      }

      const [statsRes, workdaysRes, paymentsRes] = await Promise.all([
        fetch(statsUrl, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(workdaysUrl, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(paymentsUrl, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);

      const statsData = await statsRes.json();
      const workdaysData = await workdaysRes.json();
      const paymentsData = await paymentsRes.json();

      setStats(statsData);
      setWorkdays(workdaysData.workdays || []);
      setPayments(paymentsData.payments || []);
    } catch (error) {
      console.error('Error fetching worker details:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (start, end) => {
    setDateFilter({ start, end });
    fetchWorkerDetails(start, end);
  };

  const handleClearFilter = () => {
    setDateFilter({ start: '', end: '' });
    fetchWorkerDetails('', '');
  };

  const isFiltered = dateFilter.start || dateFilter.end;

  return (
    <div>
      <div className="worker-detail-nav">
        <button onClick={onBack} className="btn btn-secondary btn-flexible worker-back-btn">← رجوع</button>
        <div className="worker-actions">
          <button onClick={() => setShowEditModal(true)} className="btn btn-primary btn-sm btn-flexible">
            ✏️ تعديل
          </button>
          <button onClick={() => setShowFilterModal(true)} className="btn btn-filter btn-sm btn-flexible">
            📅 فلتر
            {isFiltered && <span className="filter-badge-sm">✓</span>}
          </button>
          <button onClick={() => setShowPasswordModal(true)} className="btn btn-outline btn-sm btn-flexible">
            🔑 كود الدخول
          </button>
          <button onClick={() => setShowDeleteModal(true)} className="btn btn-danger btn-sm btn-flexible">
            🗑️ حذف العامل
          </button>
        </div>
      </div>

      {isFiltered && (
        <div className="filter-indicator-sm">
          📊 عرض البيانات من {dateFilter.start || 'البداية'} إلى {dateFilter.end || 'الآن'}
          <button onClick={handleClearFilter} className="clear-filter-btn">✕</button>
        </div>
      )}
      
      <div className="worker-detail-header">
        <div>
          <h2>{worker.name}</h2>
          <div className="worker-info-compact">
            <span>الأجر اليومي: ₪{worker.daily_wage.toFixed(2)}</span>
            {worker.contact && <span className="info-separator">•</span>}
            {worker.contact && <span>هاتف: {worker.contact}</span>}
            {worker.national_id && <span className="info-separator">•</span>}
            {worker.national_id && <span>رقم الهوية: {worker.national_id}</span>}
          </div>
        </div>
      </div>

      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-value">{stats?.workdays?.total_days ?? 0}</div>
          <div className="stat-label">
            إجمالي أيام العمل {isFiltered ? '(الفترة المحددة)' : ''}
          </div>
          <div className="stat-detail">
            كامل: {stats?.workdays?.full_days ?? 0} | نصف: {stats?.workdays?.half_days ?? 0}
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-value">₪{(stats?.workdays?.total_earned ?? 0).toFixed(2)}</div>
          <div className="stat-label">
            إجمالي المستحقات {isFiltered ? '(الفترة المحددة)' : ''}
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-value">₪{(stats?.payments?.total_paid ?? 0).toFixed(2)}</div>
          <div className="stat-label">
            إجمالي المدفوع {isFiltered ? '(الفترة المحددة)' : ''}
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-value">₪{stats?.payments?.balance_due?.toFixed(2) || '0.00'}</div>
          <div className="stat-label">
            الرصيد المتبقي {isFiltered ? '(للفترة المحددة)' : ''}
          </div>
        </div>
      </div>

      <div className="tabs">
        <button 
          className={`tab ${activeTab === 'workdays' ? 'active' : ''}`}
          onClick={() => setActiveTab('workdays')}
        >
          أيام العمل
        </button>
        <button 
          className={`tab ${activeTab === 'payments' ? 'active' : ''}`}
          onClick={() => setActiveTab('payments')}
        >
          المدفوعات
        </button>
      </div>

      <div className="tab-content">
        {activeTab === 'workdays' && (
          <div>
            <div className="view-header" style={{ justifyContent: 'flex-start' }}>
              <button onClick={() => setShowAddWorkdayModal(true)} className="btn btn-primary btn-flexible">+ تسجيل يوم عمل</button>
            </div>
            <div className="workdays-list">
              {workdays.length === 0 ? (
                <div className="empty-state">لا توجد أيام عمل مسجلة</div>
              ) : (
                workdays.sort((a, b) => b.date.localeCompare(a.date)).map(workday => {
                  const project = projects.find(p => p.id === workday.project_id);
                  // Check if workday is within 30 days
                  const workdayDate = new Date(workday.date);
                  const today = new Date();
                  const daysAgo = Math.floor((today - workdayDate) / (1000 * 60 * 60 * 24));
                  const canEdit = daysAgo <= 30;
                  
                  return (
                    <div key={workday.id} className="workday-item">
                      <div className="workday-date">{workday.date}</div>
                      <div className="workday-status">
                        <span className={`status-badge status-${workday.status}`}>
                          {workday.status === 'full' ? 'يوم كامل' : workday.status === 'half' ? 'نصف يوم' : 'لم يعمل'}
                        </span>
                      </div>
                      {project && <div className="workday-project">{project.name}</div>}
                      <div className="workday-amount">₪{workday.earned_amount.toFixed(2)}</div>
                      {canEdit && (
                        <button 
                          className="btn-icon-edit"
                          onClick={() => {
                            setSelectedWorkday(workday);
                            setShowEditWorkdayModal(true);
                          }}
                          title="تعديل"
                        >
                          ✏️
                        </button>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          </div>
        )}

        {activeTab === 'payments' && (
          <div>
            <div className="view-header" style={{ justifyContent: 'flex-start' }}>
              <button onClick={() => setShowAddPaymentModal(true)} className="btn btn-primary btn-flexible">+ إضافة دفعة</button>
            </div>
            <div className="payments-list">
              {payments.length === 0 ? (
                <div className="empty-state">لا توجد مدفوعات</div>
              ) : (
                payments.sort((a, b) => b.date.localeCompare(a.date)).map(payment => (
                  <div key={payment.id} className={`payment-item ${payment.status === 'pending' ? 'pending' : ''}`}>
                    <div className="payment-header">
                      <div>
                        <div className="payment-amount">₪{payment.amount.toFixed(2)}</div>
                        <div className="payment-type">{payment.type === 'deposit' ? 'دفعة مقدمة' : payment.type === 'salary' ? 'راتب' : 'دفعة'}</div>
                      </div>
                      <div className="payment-date">{payment.date}</div>
                    </div>
                    {payment.notes && <div className="payment-notes">{payment.notes}</div>}
                    <div className="payment-footer">
                      <div>
                        {payment.status === 'pending' ? (
                          <span className="status-badge status-pending">في انتظار القبول</span>
                        ) : (
                          <span className="status-badge status-accepted">تم القبول</span>
                        )}
                      </div>
                      <button 
                        className="btn-icon-edit"
                        onClick={() => {
                          setSelectedPayment(payment);
                          setShowEditPaymentModal(true);
                        }}
                        title="تعديل المبلغ"
                      >
                        ✏️
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        )}
      </div>

      {showAddWorkdayModal && (
        <AddWorkdayModal 
          worker={worker}
          projects={projects}
          onClose={() => setShowAddWorkdayModal(false)}
          onSuccess={() => { 
            setShowAddWorkdayModal(false); 
            fetchWorkerDetails(dateFilter.start, dateFilter.end);
            onRefresh(); // Refresh parent dashboard
          }}
        />
      )}

      {showEditWorkdayModal && selectedWorkday && (
        <EditWorkdayModal 
          workday={selectedWorkday}
          worker={worker}
          projects={projects}
          onClose={() => { setShowEditWorkdayModal(false); setSelectedWorkday(null); }}
          onSuccess={() => { 
            setShowEditWorkdayModal(false); 
            setSelectedWorkday(null); 
            fetchWorkerDetails(dateFilter.start, dateFilter.end);
            onRefresh(); // Refresh parent dashboard
          }}
        />
      )}

      {showAddPaymentModal && (
        <AddPaymentModal 
          worker={worker}
          onClose={() => setShowAddPaymentModal(false)}
          onSuccess={() => { 
            setShowAddPaymentModal(false); 
            fetchWorkerDetails(dateFilter.start, dateFilter.end);
            onRefresh(); // Refresh parent dashboard
          }}
        />
      )}

      {showEditPaymentModal && selectedPayment && (
        <EditPaymentModal 
          payment={selectedPayment}
          worker={worker}
          onClose={() => { setShowEditPaymentModal(false); setSelectedPayment(null); }}
          onSuccess={() => { 
            setShowEditPaymentModal(false); 
            setSelectedPayment(null); 
            fetchWorkerDetails(dateFilter.start, dateFilter.end);
            onRefresh(); // Refresh parent dashboard
          }}
        />
      )}

      {showPasswordModal && (
        <WorkerPasswordModal
          worker={worker}
          onClose={() => setShowPasswordModal(false)}
        />
      )}

      {showFilterModal && (
        <DateFilterModal 
          onClose={() => setShowFilterModal(false)}
          onApply={(start, end) => {
            handleFilterChange(start, end);
            setShowFilterModal(false);
          }}
          currentStart={dateFilter.start}
          currentEnd={dateFilter.end}
        />
      )}

      {showEditModal && (
        <EditWorkerModal
          worker={worker}
          onClose={() => setShowEditModal(false)}
          onSuccess={() => {
            setShowEditModal(false);
            onRefresh();
            fetchWorkerDetails(dateFilter.start, dateFilter.end);
          }}
        />
      )}

      {showDeleteModal && (
        <DeleteWorkerModal
          worker={worker}
          onClose={() => setShowDeleteModal(false)}
          onSuccess={() => {
            setShowDeleteModal(false);
            onRefresh(); // Refresh the workers list
            onBack(); // Return to workers list after successful deletion
          }}
        />
      )}
    </div>
  );
}

// Add Workday Modal
function AddWorkdayModal({ worker, projects, onClose, onSuccess }) {
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  const [status, setStatus] = useState('full');
  const [projectId, setProjectId] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    const token = localStorage.getItem('token');
    try {
      const response = await fetch(`${API_URL}/api/workdays`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          worker_id: worker.id,
          date,
          status,
          project_id: projectId || null
        })
      });

      const data = await response.json();

      if (response.ok) {
        onSuccess();
      } else {
        setError(data.detail || 'حدث خطأ');
      }
    } catch (error) {
      setError('حدث خطأ في الاتصال');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <h2>تسجيل يوم عمل</h2>
        <p>العامل: {worker.name}</p>
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>التاريخ</label>
            <input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              required
            />
          </div>
          <div className="form-group">
            <label>الحالة</label>
            <select value={status} onChange={(e) => setStatus(e.target.value)} required>
              <option value="full">يوم كامل</option>
              <option value="half">نصف يوم</option>
              <option value="none">لم يعمل</option>
            </select>
          </div>
          {status !== 'none' && (
            <div className="form-group">
              <label>المشروع</label>
              <select value={projectId} onChange={(e) => setProjectId(e.target.value)}>
                <option value="">-- اختر مشروع --</option>
                {projects.filter(p => p.status === 'active').map(project => (
                  <option key={project.id} value={project.id}>{project.name}</option>
                ))}
              </select>
            </div>
          )}

          {error && <div className="error-message">{error}</div>}

          <div className="modal-actions">
            <button type="button" onClick={onClose} className="btn btn-secondary">إلغاء</button>
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? 'جاري الحفظ...' : 'حفظ'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Edit Workday Modal
function EditWorkdayModal({ workday, worker, projects, onClose, onSuccess }) {
  const [status, setStatus] = useState(workday.status);
  const [projectId, setProjectId] = useState(workday.project_id || '');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    const token = localStorage.getItem('token');
    try {
      const response = await fetch(`${API_URL}/api/workdays/${workday.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          status,
          project_id: projectId || null
        })
      });

      const data = await response.json();

      if (response.ok) {
        onSuccess();
      } else {
        setError(data.detail || 'حدث خطأ');
      }
    } catch (error) {
      setError('حدث خطأ في الاتصال');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <h2>تعديل يوم العمل</h2>
        <p>العامل: {worker.name}</p>
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>التاريخ</label>
            <input
              type="date"
              value={workday.date}
              disabled
              style={{ backgroundColor: '#f0f0f0', cursor: 'not-allowed' }}
            />
            <small style={{ color: '#666', fontSize: '0.85rem' }}>لا يمكن تعديل التاريخ</small>
          </div>
          <div className="form-group">
            <label>الحالة</label>
            <select value={status} onChange={(e) => setStatus(e.target.value)} required>
              <option value="full">يوم كامل</option>
              <option value="half">نصف يوم</option>
              <option value="none">لم يعمل</option>
            </select>
          </div>
          {status !== 'none' && (
            <div className="form-group">
              <label>المشروع</label>
              <select value={projectId} onChange={(e) => setProjectId(e.target.value)}>
                <option value="">-- اختر مشروع --</option>
                {projects.filter(p => p.status === 'active').map(project => (
                  <option key={project.id} value={project.id}>{project.name}</option>
                ))}
              </select>
            </div>
          )}

          {error && <div className="error-message">{error}</div>}

          <div className="modal-actions">
            <button type="button" onClick={onClose} className="btn btn-secondary">إلغاء</button>
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? 'جاري الحفظ...' : 'حفظ التعديلات'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Add Payment Modal
function AddPaymentModal({ worker, onClose, onSuccess }) {
  const [amount, setAmount] = useState('');
  const [type, setType] = useState('deposit');
  const [notes, setNotes] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    const token = localStorage.getItem('token');
    try {
      const response = await fetch(`${API_URL}/api/payments`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          worker_id: worker.id,
          amount: parseFloat(amount),
          type,
          notes: notes || null
        })
      });

      const data = await response.json();

      if (response.ok) {
        onSuccess();
      } else {
        setError(data.detail || 'حدث خطأ');
      }
    } catch (error) {
      setError('حدث خطأ في الاتصال');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <h2>إضافة دفعة</h2>
        <p>العامل: {worker.name}</p>
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>المبلغ (₪) *</label>
            <input
              type="number"
              step="0.01"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              required
            />
          </div>
          <div className="form-group">
            <label>النوع</label>
            <select value={type} onChange={(e) => setType(e.target.value)} required>
              <option value="deposit">دفعة مقدمة</option>
              <option value="salary">راتب</option>
              <option value="advance">سلفة</option>
            </select>
          </div>
          <div className="form-group">
            <label>ملاحظات</label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows="3"
              placeholder="أي ملاحظات إضافية..."
            />
          </div>

          {error && <div className="error-message">{error}</div>}

          <div className="modal-actions">
            <button type="button" onClick={onClose} className="btn btn-secondary">إلغاء</button>
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? 'جاري الإضافة...' : 'إضافة'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Edit Payment Modal
function EditPaymentModal({ payment, worker, onClose, onSuccess }) {
  const [amount, setAmount] = useState(payment.amount.toString());
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    const token = localStorage.getItem('token');
    try {
      const response = await fetch(`${API_URL}/api/payments/${payment.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          amount: parseFloat(amount)
        })
      });

      const data = await response.json();

      if (response.ok) {
        onSuccess();
      } else {
        setError(data.detail || 'حدث خطأ');
      }
    } catch (error) {
      setError('حدث خطأ في الاتصال');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <h2>تعديل الدفعة</h2>
        <p>العامل: {worker.name}</p>
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>المبلغ (₪) *</label>
            <input
              type="number"
              step="0.01"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              required
            />
          </div>
          
          <div className="form-group">
            <label>النوع</label>
            <input
              type="text"
              value={payment.type === 'deposit' ? 'دفعة مقدمة' : payment.type === 'salary' ? 'راتب' : 'سلفة'}
              disabled
              style={{ backgroundColor: '#f0f0f0', cursor: 'not-allowed' }}
            />
            <small style={{ color: '#666', fontSize: '0.85rem' }}>لا يمكن تعديل النوع</small>
          </div>

          <div className="form-group">
            <label>التاريخ</label>
            <input
              type="text"
              value={payment.date}
              disabled
              style={{ backgroundColor: '#f0f0f0', cursor: 'not-allowed' }}
            />
            <small style={{ color: '#666', fontSize: '0.85rem' }}>لا يمكن تعديل التاريخ</small>
          </div>

          <div style={{ 
            padding: '12px', 
            backgroundColor: '#fff3cd', 
            border: '1px solid #ffc107', 
            borderRadius: '4px',
            marginBottom: '16px',
            fontSize: '14px',
            color: '#856404'
          }}>
            ⚠️ عند تعديل المبلغ، سيحتاج العامل لقبول الدفعة مرة أخرى
          </div>

          {error && <div className="error-message">{error}</div>}

          <div className="modal-actions">
            <button type="button" onClick={onClose} className="btn btn-secondary">إلغاء</button>
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? 'جاري الحفظ...' : 'حفظ التعديلات'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Projects View
// Attendance List View (Matrix of Workers vs Days)
function AttendanceListView({ workers, dateFilter, onFilterChange, onClearFilter }) {
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [attendanceData, setAttendanceData] = useState({});
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState([]);
  const [localFilter, setLocalFilter] = useState({ start: '', end: '' });
  const [selectedWorkers, setSelectedWorkers] = useState([]);
  const [showWorkerSelector, setShowWorkerSelector] = useState(false);

  // Initialize with current month on component mount
  useEffect(() => {
    const today = new Date();
    const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
    const lastDay = new Date(today.getFullYear(), today.getMonth() + 1, 0);
    
    const start = firstDay.toISOString().split('T')[0];
    const end = lastDay.toISOString().split('T')[0];
    
    setLocalFilter({ start, end });
    // Select all workers by default
    setSelectedWorkers(workers.map(w => w.id));
    fetchAttendanceData(start, end);
  }, []);

  // Update when parent filter changes
  useEffect(() => {
    if (dateFilter.start && dateFilter.end) {
      setLocalFilter({ start: dateFilter.start, end: dateFilter.end });
      fetchAttendanceData(dateFilter.start, dateFilter.end);
    }
  }, [dateFilter.start, dateFilter.end]);

  const fetchAttendanceData = async (startDate, endDate) => {
    setLoading(true);
    const token = getTokenFromAnySource();
    
    try {
      // Generate date range first
      const dates = [];
      const startParts = startDate.split('-');
      const endParts = endDate.split('-');
      const start = new Date(parseInt(startParts[0]), parseInt(startParts[1]) - 1, parseInt(startParts[2]));
      const end = new Date(parseInt(endParts[0]), parseInt(endParts[1]) - 1, parseInt(endParts[2]));
      
      const current = new Date(start);
      while (current <= end) {
        const year = current.getFullYear();
        const month = String(current.getMonth() + 1).padStart(2, '0');
        const day = String(current.getDate()).padStart(2, '0');
        dates.push(`${year}-${month}-${day}`);
        current.setDate(current.getDate() + 1);
      }
      setDateRange(dates);
      
      // Fetch workdays for all workers in the date range
      const response = await fetch(`${API_URL}/api/workdays?start_date=${startDate}&end_date=${endDate}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!response.ok) {
        console.error('Failed to fetch workdays');
        setAttendanceData({});
        return;
      }
      
      const data = await response.json();
      const workdays = data.workdays || data || [];
      
      console.log('📊 Fetched workdays:', workdays.length, 'for period:', startDate, 'to', endDate);
      
      // Organize data by worker and date
      const attendance = {};
      workdays.forEach(workday => {
        if (!attendance[workday.worker_id]) {
          attendance[workday.worker_id] = {};
        }
        attendance[workday.worker_id][workday.date] = workday.status;
      });
      
      console.log('📊 Organized attendance data for', Object.keys(attendance).length, 'workers');
      setAttendanceData(attendance);
    } catch (error) {
      console.error('Error fetching attendance data:', error);
      setAttendanceData({});
    } finally {
      setLoading(false);
    }
  };

  const handleApplyFilter = (start, end, workers) => {
    setLocalFilter({ start, end });
    setSelectedWorkers(workers);
    fetchAttendanceData(start, end);
    onFilterChange(start, end);
    setShowFilterModal(false);
  };

  const handleClearFilter = () => {
    // Reset to current month
    const today = new Date();
    const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
    const lastDay = new Date(today.getFullYear(), today.getMonth() + 1, 0);
    
    const start = firstDay.toISOString().split('T')[0];
    const end = lastDay.toISOString().split('T')[0];
    
    setLocalFilter({ start, end });
    fetchAttendanceData(start, end);
    onClearFilter();
  };

  const getStatusIcon = (status) => {
    const baseStyle = {
      fontSize: '24px',
      fontWeight: '900',
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: '36px',
      height: '36px',
      borderRadius: '50%',
      color: '#ffffff'
    };

    if (status === 'full') {
      return <span style={{ ...baseStyle, background: '#22c55e', boxShadow: '0 3px 8px rgba(34, 197, 94, 0.4)' }}>✓</span>;
    } else if (status === 'half') {
      return <span style={{ ...baseStyle, background: '#eab308', boxShadow: '0 3px 8px rgba(234, 179, 8, 0.4)' }}>½</span>;
    } else {
      return <span style={{ ...baseStyle, background: '#ef4444', boxShadow: '0 3px 8px rgba(239, 68, 68, 0.4)' }}>✗</span>;
    }
  };

  const removeWorker = (workerId) => {
    setSelectedWorkers(selectedWorkers.filter(id => id !== workerId));
  };

  const getWorkerName = (workerId) => {
    const worker = workers.find(w => w.id === workerId);
    return worker ? worker.name : '';
  };

  // Filter workers to display based on selection
  const displayedWorkers = workers.filter(w => selectedWorkers.includes(w.id));

  return (
    <div>
      <div className="view-header" style={{ justifyContent: 'flex-start', gap: '10px' }}>
        <button onClick={() => setShowFilterModal(true)} className="btn btn-filter">
          📅 فلتر التاريخ والعمال
        </button>
      </div>

      {loading ? (
        <div className="loading-state">
          <div className="spinner"></div>
          <p>جاري التحميل...</p>
        </div>
      ) : (
        <>
          <div className="filter-indicator">
            📊 عرض الحضور من {localFilter.start} إلى {localFilter.end}
            {selectedWorkers.length < workers.length && (
              <span style={{ marginRight: '10px' }}>
                • {selectedWorkers.length} من {workers.length} عامل
              </span>
            )}
          </div>

          {/* Selected Workers Display */}
          {selectedWorkers.length > 0 && selectedWorkers.length < workers.length && (
            <div className="bulk-section" style={{ marginBottom: '15px' }}>
              <div className="section-header-row">
                <h3 className="section-title-modern">👥 العمال المحددين</h3>
              </div>
              <div className="selected-workers-badges">
                {selectedWorkers.map(workerId => (
                  <div key={workerId} className="worker-badge">
                    <span>{getWorkerName(workerId)}</span>
                    <button
                      type="button"
                      onClick={() => removeWorker(workerId)}
                      className="badge-remove"
                    >
                      ✕
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {workers.length === 0 ? (
            <div className="empty-state">
              لا يوجد عمال في النظام
            </div>
          ) : displayedWorkers.length === 0 ? (
            <div className="empty-state">
              لم يتم اختيار عمال لعرضهم
            </div>
          ) : (
            <div className="attendance-matrix-wrapper">
              {/* Top Scroll Controller */}
              <div 
                className="top-scroll-controller" 
                style={{ 
                  overflowX: 'auto', 
                  overflowY: 'hidden', 
                  marginBottom: '10px',
                  borderRadius: '8px',
                  background: '#f3f4f6'
                }}
                onScroll={(e) => {
                  const bottomScroll = document.querySelector('.attendance-matrix-scroll');
                  if (bottomScroll) {
                    bottomScroll.scrollLeft = e.target.scrollLeft;
                  }
                }}
              >
                <div style={{ 
                  height: '10px', 
                  width: document.querySelector('.attendance-table')?.scrollWidth || '100%' 
                }}></div>
              </div>

              <div 
                className="attendance-matrix-scroll" 
                style={{ overflow: 'auto' }}
              >
                <table className="attendance-table" style={{ borderSpacing: '10px 0', borderCollapse: 'separate' }}>
                  <thead>
                    <tr>
                      <th className="attendance-header-corner" style={{ 
                        minWidth: '120px', 
                        background: '#ffffff', 
                        position: 'sticky', 
                        top: 0, 
                        left: 0, 
                        zIndex: 11,
                        boxShadow: '2px 2px 4px rgba(0,0,0,0.1)'
                      }}>التاريخ \ العامل</th>
                      {displayedWorkers.map(worker => (
                        <th key={worker.id} className="attendance-worker-header" style={{ 
                          minWidth: '100px', 
                          background: '#ffffff',
                          position: 'sticky',
                          top: 0,
                          zIndex: 10,
                          boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                        }}>
                          <div className="worker-header-content">
                            <span className="worker-name">{worker.name}</span>
                          </div>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {dateRange.map((date, index) => (
                      <tr key={date} style={{ background: index % 2 === 0 ? '#ffffff' : '#fafbfc' }}>
                        <td className="attendance-date-cell" style={{ 
                          background: index % 2 === 0 ? '#ffffff' : '#fafbfc', 
                          textAlign: 'center', 
                          minWidth: '120px',
                          position: 'sticky',
                          left: 0,
                          zIndex: 5,
                          boxShadow: '2px 0 4px rgba(0,0,0,0.05)'
                        }}>
                          <div className="date-cell-content">
                            {new Date(date).toLocaleDateString('ar-EG', { 
                              weekday: 'short', 
                              month: 'short', 
                              day: 'numeric' 
                            })}
                          </div>
                        </td>
                        {displayedWorkers.map(worker => {
                          const status = attendanceData[worker.id]?.[date];
                          return (
                            <td key={`${worker.id}-${date}`} className="attendance-cell" style={{ textAlign: 'center', verticalAlign: 'middle', padding: '12px', background: index % 2 === 0 ? '#ffffff' : '#fafbfc', minWidth: '100px' }}>
                              {getStatusIcon(status)}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              
              <div className="attendance-legend">
                <div className="legend-title">🔖 المفتاح:</div>
                <div className="legend-items">
                  <div className="legend-item">
                    <span className="attendance-icon attendance-full">✓</span>
                    <span>يوم كامل</span>
                  </div>
                  <div className="legend-item">
                    <span className="attendance-icon attendance-half">½</span>
                    <span>نصف يوم</span>
                  </div>
                  <div className="legend-item">
                    <span className="attendance-icon attendance-none">✗</span>
                    <span>لم يعمل</span>
                  </div>
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {showFilterModal && (
        <AttendanceFilterModal 
          workers={workers}
          selectedWorkers={selectedWorkers}
          onClose={() => setShowFilterModal(false)}
          onApply={handleApplyFilter}
          currentStart={localFilter.start}
          currentEnd={localFilter.end}
        />
      )}

      {showWorkerSelector && (
        <WorkerSelectorModal
          workers={workers}
          selectedWorkers={selectedWorkers}
          onClose={() => setShowWorkerSelector(false)}
          onSelect={setSelectedWorkers}
        />
      )}
    </div>
  );
}


function ProjectsView({ projects, onRefresh }) {
  const [showAddModal, setShowAddModal] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all'); // 'all', 'active', 'finished'

  // Filter projects based on selected status
  const filteredProjects = statusFilter === 'all' 
    ? projects 
    : projects.filter(p => p.status === statusFilter);

  const handleStatusChange = async (projectId, newStatus) => {
    const token = localStorage.getItem('token');
    try {
      const response = await fetch(`${API_URL}/api/projects/${projectId}/status?status=${newStatus}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        onRefresh();
      } else {
        const data = await response.json();
        alert(data.detail || 'حدث خطأ');
      }
    } catch (error) {
      alert('حدث خطأ في الاتصال');
    }
  };

  return (
    <div>
      <div className="view-header-projects">
        <button onClick={() => setShowAddModal(true)} className="btn btn-primary btn-flexible">+ إضافة مشروع</button>
        <div className="filter-buttons">
          <button 
            onClick={() => setStatusFilter('all')} 
            className={`filter-btn ${statusFilter === 'all' ? 'active' : ''}`}
          >
            الكل ({projects.length})
          </button>
          <button 
            onClick={() => setStatusFilter('active')} 
            className={`filter-btn ${statusFilter === 'active' ? 'active' : ''}`}
          >
            نشط ({projects.filter(p => p.status === 'active').length})
          </button>
          <button 
            onClick={() => setStatusFilter('finished')} 
            className={`filter-btn ${statusFilter === 'finished' ? 'active' : ''}`}
          >
            منتهي ({projects.filter(p => p.status === 'finished').length})
          </button>
        </div>
      </div>

      {filteredProjects.length === 0 ? (
        <div className="empty-state">
          {statusFilter === 'all' 
            ? 'لا توجد مشاريع. ابدأ بإضافة مشروع جديد.' 
            : `لا توجد مشاريع ${statusFilter === 'active' ? 'نشطة' : 'منتهية'}.`}
        </div>
      ) : (
        <div className="projects-list">
          {filteredProjects.map(project => (
            <div key={project.id} className="project-card">
              <div className="project-header-row">
                <div className="project-info">
                  <h3>{project.name}</h3>
                  <span className={`project-status-badge ${project.status}`}>
                    {project.status === 'active' ? '🟢 نشط' : '⚫ منتهي'}
                  </span>
                </div>
                <div className="project-actions-dropdown">
                  <button className="dropdown-toggle">⋮</button>
                  <div className="dropdown-menu">
                    {project.status === 'active' ? (
                      <button onClick={() => handleStatusChange(project.id, 'finished')}>
                        ✓ إنهاء المشروع
                      </button>
                    ) : (
                      <button onClick={() => handleStatusChange(project.id, 'active')}>
                        ↻ إعادة فتح المشروع
                      </button>
                    )}
                  </div>
                </div>
              </div>
              {project.start_date && <div className="project-date">تاريخ البدء: {project.start_date}</div>}
              {project.description && <div className="project-description">{project.description}</div>}
            </div>
          ))}
        </div>
      )}

      {showAddModal && (
        <AddProjectModal 
          onClose={() => setShowAddModal(false)}
          onSuccess={() => { setShowAddModal(false); onRefresh(); }}
        />
      )}
    </div>
  );
}

// Add Project Modal
function AddProjectModal({ onClose, onSuccess }) {
  const [name, setName] = useState('');
  const [startDate, setStartDate] = useState('');
  const [description, setDescription] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    const token = localStorage.getItem('token');
    try {
      const response = await fetch(`${API_URL}/api/projects`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          name,
          start_date: startDate || null,
          description: description || null
        })
      });

      const data = await response.json();

      if (response.ok) {
        onSuccess();
      } else {
        setError(data.detail || 'حدث خطأ');
      }
    } catch (error) {
      setError('حدث خطأ في الاتصال');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <h2>إضافة مشروع جديد</h2>
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>اسم المشروع *</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
            />
          </div>
          <div className="form-group">
            <label>تاريخ البدء</label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
            />
          </div>
          <div className="form-group">
            <label>الوصف</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows="3"
            />
          </div>

          {error && <div className="error-message">{error}</div>}

          <div className="modal-actions">
            <button type="button" onClick={onClose} className="btn btn-secondary">إلغاء</button>
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? 'جاري الإضافة...' : 'إضافة'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Worker Password Modal
// Edit Worker Modal
function EditWorkerModal({ worker, onClose, onSuccess }) {
  const [name, setName] = useState(worker.name);
  const [nationalId, setNationalId] = useState(worker.national_id || '');
  const [phone, setPhone] = useState(worker.phone || '');
  const [dailyWage, setDailyWage] = useState(worker.daily_wage);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    if (!name.trim()) {
      setError('يرجى إدخال اسم العامل');
      return;
    }
    
    if (!dailyWage || dailyWage <= 0) {
      setError('يرجى إدخال أجر يومي صحيح');
      return;
    }

    setLoading(true);
    const token = localStorage.getItem('token');

    try {
      const response = await fetch(`${API_URL}/api/workers/${worker.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          name: name.trim(),
          national_id: nationalId.trim() || null,
          phone: phone.trim() || null,
          daily_wage: parseFloat(dailyWage)
        })
      });

      const data = await response.json();
      
      if (response.ok) {
        onSuccess();
      } else {
        setError(data.detail || 'حدث خطأ في تحديث بيانات العامل');
      }
    } catch (error) {
      setError('حدث خطأ في الاتصال');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal modal-password" onClick={(e) => e.stopPropagation()}>
        <div className="password-modal-header">
          <h2>✏️ تعديل بيانات العامل</h2>
          <button onClick={onClose} className="close-btn-simple">✕</button>
        </div>

        <div className="password-modal-body">
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label>الاسم *</label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                className="form-input"
              />
            </div>

            <div className="form-group">
              <label>رقم الهوية</label>
              <input
                type="text"
                value={nationalId}
                onChange={(e) => setNationalId(e.target.value)}
                className="form-input"
              />
            </div>

            <div className="form-group">
              <label>رقم الهاتف</label>
              <input
                type="text"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                className="form-input"
              />
            </div>

            <div className="form-group">
              <label>الأجر اليومي (₪) *</label>
              <input
                type="number"
                value={dailyWage}
                onChange={(e) => setDailyWage(e.target.value)}
                min="0"
                step="0.01"
                required
                className="form-input"
              />
            </div>

            {error && <div className="error-message">{error}</div>}

            <div className="modal-actions">
              <button type="button" onClick={onClose} className="btn btn-secondary">
                إلغاء
              </button>
              <button type="submit" className="btn btn-primary" disabled={loading}>
                {loading ? 'جاري الحفظ...' : '✓ حفظ التغييرات'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

function WorkerPasswordModal({ worker, onClose }) {
  const [currentCode, setCurrentCode] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [resetLoading, setResetLoading] = useState(false);

  useEffect(() => {
    fetchWorkerCode();
  }, []);

  const fetchWorkerCode = async () => {
    setLoading(true);
    const token = localStorage.getItem('token');
    
    try {
      const response = await fetch(`${API_URL}/api/workers/${worker.id}/code`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      
      if (response.ok && data.code_exists) {
        // Code exists but is hashed, show message
        setCurrentCode(null);
      }
    } catch (error) {
      console.error('Error fetching code:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleResetCode = async () => {
    setResetLoading(true);
    const token = localStorage.getItem('token');
    
    try {
      const response = await fetch(`${API_URL}/api/workers/${worker.id}/reset-code`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      const data = await response.json();
      if (response.ok) {
        setCurrentCode(data.code);
      }
    } catch (error) {
      console.error('Error resetting code:', error);
    } finally {
      setResetLoading(false);
    }
  };

  const handleCopy = (code) => {
    // Fallback method for environments where Clipboard API is blocked
    const textArea = document.createElement('textarea');
    textArea.value = code;
    textArea.style.position = 'fixed';
    textArea.style.left = '-999999px';
    textArea.style.top = '-999999px';
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();
    
    try {
      document.execCommand('copy');
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
      alert('فشل النسخ. يرجى نسخ الكود يدوياً: ' + code);
    } finally {
      document.body.removeChild(textArea);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal modal-password" onClick={(e) => e.stopPropagation()}>
        <div className="password-modal-header">
          <h2>🔑 كود دخول العامل</h2>
          <button onClick={onClose} className="close-btn-simple">✕</button>
        </div>

        <div className="password-modal-body">
          <div className="worker-info-box">
            <span className="worker-avatar">👤</span>
            <div>
              <h3>{worker.name}</h3>
              <p>{worker.national_id || 'لا يوجد رقم هوية'}</p>
            </div>
          </div>

          {loading ? (
            <div className="loading-state">
              <div className="spinner"></div>
              <p>جاري التحميل...</p>
            </div>
          ) : currentCode ? (
            <div className="password-display">
              <div className="password-label">
                <span className="label-icon">🔑</span>
                <h3>كود الدخول الحالي</h3>
              </div>
              <div className="code-box">
                <span className="code-text">{currentCode}</span>
                <button onClick={() => handleCopy(currentCode)} className="copy-btn" title="نسخ الكود">
                  {copied ? '✓' : '📋'}
                </button>
              </div>
              {copied && <div className="copied-message">✅ تم نسخ الكود بنجاح!</div>}
              <p className="password-hint">شارك هذا الكود مع العامل لتسجيل الدخول</p>
            </div>
          ) : (
            <div className="password-info">
              <div className="warning-box">
                <p className="info-icon">🔒</p>
                <p className="info-text">
                  <strong>كود الدخول مشفر ولا يمكن عرضه</strong><br/>
                  لأسباب أمنية، يتم تخزين أكواد الدخول بشكل مشفر.<br/>
                  يمكنك إنشاء كود جديد لعرضه ومشاركته مع العامل.
                </p>
              </div>
              <div className="password-actions">
                <button 
                  onClick={handleResetCode} 
                  className="btn btn-primary btn-large"
                  disabled={resetLoading}
                >
                  {resetLoading ? 'جاري الإنشاء...' : '🔄 إنشاء كود جديد'}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// Simple Delete Worker Modal with single confirmation
function DeleteWorkerModal({ worker, onClose, onSuccess }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [workerData, setWorkerData] = useState(null);

  // Fetch worker statistics on mount
  useEffect(() => {
    const fetchWorkerStats = async () => {
      const token = localStorage.getItem('token');
      try {
        const response = await fetch(`${API_URL}/api/analytics/worker/${worker.id}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (response.ok) {
          const data = await response.json();
          setWorkerData(data);
        }
      } catch (error) {
        console.error('Error fetching worker data:', error);
      }
    };
    fetchWorkerStats();
  }, [worker.id]);

  const handleDelete = async () => {
    setLoading(true);
    setError('');
    
    const token = localStorage.getItem('token');
    try {
      const response = await fetch(`${API_URL}/api/workers/${worker.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();
      
      if (response.ok) {
        onSuccess();
      } else {
        setError(data.detail || 'فشل في حذف العامل');
        setLoading(false);
      }
    } catch (error) {
      setError('حدث خطأ في الاتصال');
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={!loading ? onClose : undefined}>
      <div className="modal modal-delete" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>تأكيد حذف العامل</h2>
          {!loading && <button onClick={onClose} className="close-btn">✕</button>}
        </div>
        
        <div className="modal-body">
          {loading ? (
            <div className="delete-loading">
              <div className="loading-icon">🗑️</div>
              <h3 className="loading-title">جاري حذف العامل...</h3>
              <div className="spinner-large"></div>
              <p className="loading-message">يرجى الانتظار، جاري حذف بيانات العامل من النظام</p>
              <div className="loading-steps">
                <div className="step">✓ حذف حساب العامل</div>
                <div className="step">✓ حذف أيام العمل</div>
                <div className="step">✓ حذف المدفوعات</div>
                <div className="step">✓ تنظيف البيانات المرتبطة</div>
              </div>
            </div>
          ) : (
            <div className="delete-step">
              <div className="warning-icon">⚠️</div>
              <h3 className="warning-title">هل أنت متأكد من حذف هذا العامل؟</h3>
              
              <div className="worker-info-box">
                <h4>العامل المراد حذفه:</h4>
                <div className="worker-detail">
                  <span className="worker-name">👤 {worker.name}</span>
                  <span className="worker-id">🆔 {worker.national_id}</span>
                  <span className="worker-phone">📱 {worker.phone}</span>
                </div>
              </div>
              
              {workerData && (
                <div className="data-warning">
                  <h4>البيانات التي سيتم حذفها نهائياً:</h4>
                  <ul className="data-list">
                    <li>📊 {workerData.stats?.total_workdays || 0} يوم عمل</li>
                    <li>💰 {workerData.stats?.total_payments || 0} دفعة مالية</li>
                    <li>👤 حساب المستخدم والدخول</li>
                    <li>📱 جميع البيانات المرتبطة</li>
                  </ul>
                </div>
              )}

              <div className="warning-message">
                <p><strong>هذا الإجراء لا يمكن التراجع عنه!</strong></p>
                <p>سيتم حذف جميع بيانات العامل نهائياً من النظام.</p>
              </div>

              {error && <div className="error-message">{error}</div>}

              <div className="step-buttons">
                <button onClick={onClose} className="btn btn-secondary">
                  إلغاء
                </button>
                <button 
                  onClick={handleDelete} 
                  className="btn btn-danger"
                >
                  🗑️ حذف نهائي
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// Helper function to format dates without UTC conversion
function formatLocalDate(date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

// Date Filter Modal - Redesigned
function DateFilterModal({ onClose, onApply, currentStart, currentEnd }) {
  const [startDate, setStartDate] = useState(currentStart || '');
  const [endDate, setEndDate] = useState(currentEnd || '');
  const startDateRef = React.useRef(null);
  const endDateRef = React.useRef(null);

  const getDateRange = (preset) => {
    const today = new Date();
    let start, end;

    switch (preset) {
      case 'today':
        start = end = formatLocalDate(today);
        break;
      case 'yesterday':
        const yesterday = new Date(today);
        yesterday.setDate(yesterday.getDate() - 1);
        start = end = formatLocalDate(yesterday);
        break;
      case 'this-week':
        const weekStart = new Date(today);
        weekStart.setDate(today.getDate() - today.getDay());
        start = formatLocalDate(weekStart);
        end = formatLocalDate(today);
        break;
      case 'last-week':
        const lastWeekEnd = new Date(today);
        lastWeekEnd.setDate(today.getDate() - today.getDay() - 1);
        const lastWeekStart = new Date(lastWeekEnd);
        lastWeekStart.setDate(lastWeekEnd.getDate() - 6);
        start = formatLocalDate(lastWeekStart);
        end = formatLocalDate(lastWeekEnd);
        break;
      case 'this-month':
        start = formatLocalDate(new Date(today.getFullYear(), today.getMonth(), 1));
        end = formatLocalDate(today);
        break;
      case 'last-month':
        const lastMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1);
        start = formatLocalDate(lastMonth);
        const lastMonthEnd = new Date(today.getFullYear(), today.getMonth(), 0);
        end = formatLocalDate(lastMonthEnd);
        break;
      case 'last-3-months':
        const threeMonthsAgo = new Date(today.getFullYear(), today.getMonth() - 3, 1);
        start = formatLocalDate(threeMonthsAgo);
        end = formatLocalDate(today);
        break;
      case 'this-year':
        start = formatLocalDate(new Date(today.getFullYear(), 0, 1));
        end = formatLocalDate(today);
        break;
      default:
        return;
    }

    setStartDate(start);
    setEndDate(end);
  };

  const handleApply = () => {
    if (startDate && endDate) {
      onApply(startDate, endDate);
    }
  };

  const presets = [
    { id: 'today', label: 'اليوم', icon: '📅', color: '#667eea' },
    { id: 'yesterday', label: 'أمس', icon: '⏮️', color: '#764ba2' },
    { id: 'this-week', label: 'هذا الأسبوع', icon: '📆', color: '#28a745' },
    { id: 'last-week', label: 'الأسبوع الماضي', icon: '📋', color: '#17a2b8' },
    { id: 'this-month', label: 'هذا الشهر', icon: '📊', color: '#ffc107' },
    { id: 'last-month', label: 'الشهر الماضي', icon: '📈', color: '#fd7e14' },
  ];

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="filter-modal" onClick={(e) => e.stopPropagation()}>
        <div className="filter-modal-header">
          <div className="filter-modal-title">
            <span className="filter-icon">📅</span>
            <h2>اختر الفترة الزمنية</h2>
          </div>
          <button onClick={onClose} className="filter-close-btn">✕</button>
        </div>
        
        <div className="filter-modal-body">
          <div className="quick-filters-section">
            <h3 className="section-title">⚡ فترات سريعة</h3>
            <div className="quick-filters-grid">
              {presets.map(preset => (
                <button
                  key={preset.id}
                  onClick={() => getDateRange(preset.id)}
                  className="quick-filter-card"
                  style={{ borderColor: preset.color }}
                >
                  <span className="filter-card-icon">{preset.icon}</span>
                  <span className="filter-card-label">{preset.label}</span>
                </button>
              ))}
            </div>
          </div>

          <div className="custom-filter-section">
            <h3 className="section-title">🎯 فترة مخصصة</h3>
            <div className="date-picker-row">
              <label className="date-picker-box">
                <span className="date-label">📍 من تاريخ</span>
                <input
                  ref={startDateRef}
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="modern-date-input"
                />
              </label>
              <div className="date-arrow">←</div>
              <label className="date-picker-box">
                <span className="date-label">🏁 إلى تاريخ</span>
                <input
                  ref={endDateRef}
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="modern-date-input"
                />
              </label>
            </div>
          </div>

          {startDate && endDate && (
            <div className="filter-preview">
              <div className="preview-icon">✓</div>
              <div className="preview-text">
                <strong>الفترة المحددة:</strong>
                <span>من {startDate} إلى {endDate}</span>
              </div>
            </div>
          )}
        </div>

        <div className="filter-modal-footer">
          <button onClick={onClose} className="btn btn-outline">
            إلغاء
          </button>
          <button 
            onClick={handleApply} 
            className="btn btn-apply"
            disabled={!startDate || !endDate}
          >
            ✓ تطبيق الفلتر
          </button>
        </div>
      </div>
    </div>
  );
}


// Attendance Filter Modal - combines date filtering and worker selection
function AttendanceFilterModal({ workers, selectedWorkers, onClose, onApply, currentStart, currentEnd }) {
  const [startDate, setStartDate] = useState(currentStart || '');
  const [endDate, setEndDate] = useState(currentEnd || '');
  const [localSelectedWorkers, setLocalSelectedWorkers] = useState(selectedWorkers || []);
  const [showWorkerSelector, setShowWorkerSelector] = useState(false);

  const getDateRange = (preset) => {
    const today = new Date();
    let start, end;

    switch (preset) {
      case 'today':
        start = end = formatLocalDate(today);
        break;
      case 'this-week':
        const weekStart = new Date(today);
        weekStart.setDate(today.getDate() - today.getDay());
        start = formatLocalDate(weekStart);
        end = formatLocalDate(today);
        break;
      case 'this-month':
        start = formatLocalDate(new Date(today.getFullYear(), today.getMonth(), 1));
        end = formatLocalDate(new Date(today.getFullYear(), today.getMonth() + 1, 0));
        break;
      case 'last-month':
        const lastMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1);
        start = formatLocalDate(lastMonth);
        const lastMonthEnd = new Date(today.getFullYear(), today.getMonth(), 0);
        end = formatLocalDate(lastMonthEnd);
        break;
      default:
        return;
    }

    setStartDate(start);
    setEndDate(end);
  };

  const handleApply = () => {
    if (startDate && endDate && localSelectedWorkers.length > 0) {
      onApply(startDate, endDate, localSelectedWorkers);
    }
  };

  const removeWorker = (workerId) => {
    setLocalSelectedWorkers(localSelectedWorkers.filter(id => id !== workerId));
  };

  const getWorkerName = (workerId) => {
    const worker = workers.find(w => w.id === workerId);
    return worker ? worker.name : '';
  };

  const selectAllWorkers = () => {
    setLocalSelectedWorkers(workers.map(w => w.id));
  };

  const clearAllWorkers = () => {
    setLocalSelectedWorkers([]);
  };

  const presets = [
    { id: 'today', label: 'اليوم', icon: '📅' },
    { id: 'this-week', label: 'هذا الأسبوع', icon: '📆' },
    { id: 'this-month', label: 'هذا الشهر', icon: '📊' },
    { id: 'last-month', label: 'الشهر الماضي', icon: '📈' },
  ];

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal modal-bulk" onClick={(e) => e.stopPropagation()}>
        <div className="bulk-modal-header">
          <div>
            <h2>🔍 فلتر القائمة</h2>
            <p className="bulk-subtitle">اختر الفترة الزمنية والعمال لعرضهم</p>
          </div>
          <button onClick={onClose} className="close-btn-simple">✕</button>
        </div>
        
        <div className="bulk-form">
          {/* Workers Selection */}
          <div className="bulk-section">
            <div className="section-header-row">
              <h3 className="section-title-modern">👥 العمال ({localSelectedWorkers.length}/{workers.length})</h3>
              <div style={{ display: 'flex', gap: '8px' }}>
                <button
                  type="button"
                  onClick={selectAllWorkers}
                  className="btn btn-outline-sm"
                >
                  الكل
                </button>
                <button
                  type="button"
                  onClick={clearAllWorkers}
                  className="btn btn-outline-sm"
                >
                  مسح
                </button>
                <button
                  type="button"
                  onClick={() => setShowWorkerSelector(true)}
                  className="btn btn-outline-sm"
                >
                  + اختر عمال
                </button>
              </div>
            </div>

            {localSelectedWorkers.length > 0 ? (
              <div className="selected-workers-badges">
                {localSelectedWorkers.map(workerId => (
                  <div key={workerId} className="worker-badge">
                    <span>{getWorkerName(workerId)}</span>
                    <button
                      type="button"
                      onClick={() => removeWorker(workerId)}
                      className="badge-remove"
                    >
                      ✕
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <div className="empty-selection">
                لم يتم اختيار عمال بعد
              </div>
            )}
          </div>

          {/* Date Range */}
          <div className="bulk-section">
            <h3 className="section-title-modern">📆 الفترة الزمنية</h3>
            
            <div className="quick-filters-grid" style={{ marginBottom: '15px' }}>
              {presets.map(preset => (
                <button
                  key={preset.id}
                  onClick={() => getDateRange(preset.id)}
                  className="quick-filter-card"
                  style={{ padding: '8px 12px', fontSize: '13px' }}
                >
                  <span className="filter-card-icon">{preset.icon}</span>
                  <span className="filter-card-label">{preset.label}</span>
                </button>
              ))}
            </div>

            <div className="date-range-modern">
              <div className="date-box-modern">
                <label>من تاريخ</label>
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  required
                  className="modern-input"
                />
              </div>
              <div className="arrow-modern">←</div>
              <div className="date-box-modern">
                <label>إلى تاريخ</label>
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  required
                  className="modern-input"
                />
              </div>
            </div>
          </div>
        </div>

        <div className="filter-modal-footer">
          <button onClick={onClose} className="btn btn-outline">
            إلغاء
          </button>
          <button 
            onClick={handleApply} 
            className="btn btn-apply"
            disabled={!startDate || !endDate || localSelectedWorkers.length === 0}
          >
            ✓ تطبيق الفلتر
          </button>
        </div>

        {showWorkerSelector && (
          <WorkerSelectorModal
            workers={workers}
            selectedWorkers={localSelectedWorkers}
            onClose={() => setShowWorkerSelector(false)}
            onSelect={setLocalSelectedWorkers}
          />
        )}
      </div>
    </div>
  );
}


// Worker Selector Modal with Search
function WorkerSelectorModal({ workers, selectedWorkers, onClose, onSelect }) {
  const [searchQuery, setSearchQuery] = useState('');
  const [localSelected, setLocalSelected] = useState(selectedWorkers);
  const [showFilter, setShowFilter] = useState(false);
  const [workdayFilter, setWorkdayFilter] = useState({ date: '', dateStart: '', dateEnd: '', isRange: false, enabled: false });
  const [filteredByWorkday, setFilteredByWorkday] = useState(workers);

  // Fetch filtered workers based on workday filter
  const applyWorkdayFilter = async () => {
    const token = localStorage.getItem('token');
    try {
      let workersUrl = `${API_URL}/api/workers`;
      const params = new URLSearchParams();
      
      if (workdayFilter.date && !workdayFilter.isRange) {
        params.append('filter_date', workdayFilter.date);
      } else if (workdayFilter.dateStart && workdayFilter.dateEnd && workdayFilter.isRange) {
        params.append('filter_date_start', workdayFilter.dateStart);
        params.append('filter_date_end', workdayFilter.dateEnd);
      }
      
      if (params.toString()) {
        workersUrl += `?${params.toString()}`;
      }

      const workersRes = await fetch(workersUrl, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      const workersData = await workersRes.json();
      setFilteredByWorkday(workersData.workers || []);
      setWorkdayFilter({ ...workdayFilter, enabled: true });
      setShowFilter(false);
    } catch (error) {
      console.error('Error fetching filtered workers:', error);
    }
  };

  const clearWorkdayFilter = () => {
    setWorkdayFilter({ date: '', dateStart: '', dateEnd: '', isRange: false, enabled: false });
    setFilteredByWorkday(workers);
  };

  // Apply search query on top of workday-filtered workers
  const filteredWorkers = filteredByWorkday.filter(worker =>
    worker.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (worker.national_id && worker.national_id.includes(searchQuery)) ||
    (worker.phone && worker.phone.includes(searchQuery))
  );

  const toggleWorker = (workerId) => {
    if (localSelected.includes(workerId)) {
      setLocalSelected(localSelected.filter(id => id !== workerId));
    } else {
      setLocalSelected([...localSelected, workerId]);
    }
  };

  const selectAll = () => {
    setLocalSelected(filteredWorkers.map(w => w.id));
  };

  const clearAll = () => {
    setLocalSelected([]);
  };

  const handleConfirm = () => {
    onSelect(localSelected);
    onClose();
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal modal-bulk" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '600px' }}>
        <div className="bulk-modal-header">
          <div>
            <h2>👥 اختيار العمال</h2>
            <p className="bulk-subtitle">ابحث واختر العمال للتسجيل الجماعي</p>
          </div>
          <button onClick={onClose} className="close-btn-simple">✕</button>
        </div>

        <div className="bulk-form" style={{ padding: '24px' }}>
          {/* Workday Filter Button */}
          <div className="workday-filter-section">
            <button 
              onClick={() => setShowFilter(!showFilter)} 
              className={`btn btn-flexible ${workdayFilter.enabled ? 'btn-filter-active' : 'btn-outline'}`}
            >
              <span className="btn-icon">🔍</span>
              {workdayFilter.enabled ? 'تصفية نشطة' : 'تصفية حسب أيام العمل'}
            </button>
            {workdayFilter.enabled && (
              <button onClick={clearWorkdayFilter} className="btn btn-secondary btn-flexible">
                <span className="btn-icon">✕</span>
                إلغاء التصفية
              </button>
            )}
          </div>

          {/* Filter Options */}
          {showFilter && (
            <div className="inline-filter-box">
              <div className="filter-type-toggle">
                <button 
                  className={`toggle-btn ${!workdayFilter.isRange ? 'active' : ''}`}
                  onClick={() => setWorkdayFilter({ ...workdayFilter, isRange: false })}
                >
                  📅 تاريخ واحد
                </button>
                <button 
                  className={`toggle-btn ${workdayFilter.isRange ? 'active' : ''}`}
                  onClick={() => setWorkdayFilter({ ...workdayFilter, isRange: true })}
                >
                  📆 فترة زمنية
                </button>
              </div>

              {!workdayFilter.isRange ? (
                <div className="filter-date-input">
                  <input
                    type="date"
                    value={workdayFilter.date}
                    onChange={(e) => setWorkdayFilter({ ...workdayFilter, date: e.target.value })}
                    className="modern-date-input"
                    placeholder="اختر التاريخ"
                  />
                </div>
              ) : (
                <div className="filter-date-range-inputs">
                  <input
                    type="date"
                    value={workdayFilter.dateStart}
                    onChange={(e) => setWorkdayFilter({ ...workdayFilter, dateStart: e.target.value })}
                    className="modern-date-input"
                    placeholder="من تاريخ"
                  />
                  <span>←</span>
                  <input
                    type="date"
                    value={workdayFilter.dateEnd}
                    onChange={(e) => setWorkdayFilter({ ...workdayFilter, dateEnd: e.target.value })}
                    className="modern-date-input"
                    placeholder="إلى تاريخ"
                  />
                </div>
              )}

              <button 
                onClick={applyWorkdayFilter} 
                className="btn btn-apply"
                disabled={workdayFilter.isRange ? (!workdayFilter.dateStart || !workdayFilter.dateEnd) : !workdayFilter.date}
              >
                ✓ تطبيق
              </button>
            </div>
          )}

          {/* Active Filter Banner */}
          {workdayFilter.enabled && (
            <div className="filter-status-banner-compact">
              <div className="filter-status-icon">✓</div>
              <div className="filter-status-text">
                {workdayFilter.isRange ? (
                  <>عرض العمال الذين لم يوقعوا من {workdayFilter.dateStart} إلى {workdayFilter.dateEnd}</>
                ) : (
                  <>عرض العمال الذين لم يوقعوا في {workdayFilter.date}</>
                )}
              </div>
            </div>
          )}

          {/* Search Box */}
          <div className="search-box-modern">
            <input
              type="text"
              placeholder="🔍 ابحث بالاسم، رقم الهوية، أو الهاتف..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="search-input-modern"
              autoFocus
            />
          </div>

          {/* Select/Clear All Buttons */}
          <div className="selection-actions">
            <button onClick={selectAll} className="btn btn-outline-sm">
              ✓ تحديد الكل ({filteredWorkers.length})
            </button>
            <button onClick={clearAll} className="btn btn-outline-sm">
              ✕ إلغاء التحديد
            </button>
            <span className="selected-count">{localSelected.length} محدد</span>
          </div>

          {/* Workers List */}
          <div className="workers-selector-list">
            {filteredWorkers.length === 0 ? (
              <div className="empty-selection">
                {workdayFilter.enabled ? 'لا يوجد عمال بدون توقيع للفترة المحددة' : 'لا توجد نتائج للبحث'}
              </div>
            ) : (
              filteredWorkers.map(worker => (
                <label key={worker.id} className="worker-item-selectable">
                  <input
                    type="checkbox"
                    checked={localSelected.includes(worker.id)}
                    onChange={() => toggleWorker(worker.id)}
                  />
                  <div className="worker-item-info">
                    <span className="worker-name-select">{worker.name}</span>
                    {worker.national_id && (
                      <span className="worker-meta">هوية: {worker.national_id}</span>
                    )}
                  </div>
                  <span className="worker-wage-select">₪{worker.daily_wage}</span>
                </label>
              ))
            )}
          </div>

          {/* Footer Actions */}
          <div className="modal-actions-bulk">
            <button onClick={onClose} className="btn btn-secondary">
              إلغاء
            </button>
            <button onClick={handleConfirm} className="btn btn-success">
              ✓ تأكيد ({localSelected.length})
            </button>
          </div>
        </div>
      </div>

      {/* Workday Filter Modal (optional standalone modal) */}
    </div>
  );
}

// Bulk Multi-Worker Assignment Modal - Redesigned
function BulkMultiWorkerModal({ workers, projects, onClose, onSuccess }) {
  const [selectedWorkers, setSelectedWorkers] = useState([]);
  const [showWorkerSelector, setShowWorkerSelector] = useState(false);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [status, setStatus] = useState('full');
  const [projectId, setProjectId] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const removeWorker = (workerId) => {
    setSelectedWorkers(selectedWorkers.filter(id => id !== workerId));
  };

  const generateDateRange = (start, end) => {
    const dates = [];
    const currentDate = new Date(start);
    const endDateObj = new Date(end);
    
    while (currentDate <= endDateObj) {
      dates.push(currentDate.toISOString().split('T')[0]);
      currentDate.setDate(currentDate.getDate() + 1);
    }
    
    return dates;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (selectedWorkers.length === 0) {
      setError('يرجى اختيار عامل واحد على الأقل');
      return;
    }
    
    if (!startDate || !endDate) {
      setError('يرجى اختيار تاريخ البداية والنهاية');
      return;
    }
    
    if (new Date(startDate) > new Date(endDate)) {
      setError('تاريخ البداية يجب أن يكون قبل تاريخ النهاية');
      return;
    }

    setError('');
    setLoading(true);

    const token = localStorage.getItem('token');
    const dates = generateDateRange(startDate, endDate);

    try {
      const response = await fetch(`${API_URL}/api/workdays/bulk-multi`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          worker_ids: selectedWorkers,
          dates: dates,
          status: status,
          project_id: projectId || null
        })
      });

      const data = await response.json();

      if (response.ok) {
        onSuccess();
      } else {
        setError(data.detail || 'حدث خطأ');
      }
    } catch (error) {
      setError('حدث خطأ في الاتصال');
    } finally {
      setLoading(false);
    }
  };

  const getWorkerName = (workerId) => {
    const worker = workers.find(w => w.id === workerId);
    return worker ? worker.name : '';
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal modal-bulk" onClick={(e) => e.stopPropagation()}>
        <div className="bulk-modal-header">
          <div>
            <h2>📅 تسجيل جماعي لأيام العمل</h2>
            <p className="bulk-subtitle">سجل أيام عمل لعدة عمال في خطوة واحدة</p>
          </div>
          <button onClick={onClose} className="close-btn-simple">✕</button>
        </div>
        
        <form onSubmit={handleSubmit} className="bulk-form">
          {/* Workers Selection */}
          <div className="bulk-section">
            <div className="section-header-row">
              <h3 className="section-title-modern">👥 العمال</h3>
              <button
                type="button"
                onClick={() => setShowWorkerSelector(true)}
                className="btn btn-outline-sm"
              >
                + اختر عمال
              </button>
            </div>

            {selectedWorkers.length > 0 ? (
              <div className="selected-workers-badges">
                {selectedWorkers.map(workerId => (
                  <div key={workerId} className="worker-badge">
                    <span>{getWorkerName(workerId)}</span>
                    <button
                      type="button"
                      onClick={() => removeWorker(workerId)}
                      className="badge-remove"
                    >
                      ✕
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <div className="empty-selection">
                لم يتم اختيار عمال بعد
              </div>
            )}
          </div>

          {/* Date Range */}
          <div className="bulk-section">
            <h3 className="section-title-modern">📆 الفترة الزمنية</h3>
            <div className="date-range-modern">
              <div className="date-box-modern">
                <label>من تاريخ</label>
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  required
                  className="modern-input"
                />
              </div>
              <div className="arrow-modern">←</div>
              <div className="date-box-modern">
                <label>إلى تاريخ</label>
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  required
                  className="modern-input"
                />
              </div>
            </div>
          </div>

          {/* Work Details */}
          <div className="bulk-section">
            <h3 className="section-title-modern">⚙️ تفاصيل العمل</h3>
            <div className="work-details-row">
              <div className="detail-box">
                <label>الحالة</label>
                <select value={status} onChange={(e) => setStatus(e.target.value)} required className="modern-select">
                  <option value="full">يوم كامل ✓</option>
                  <option value="half">نصف يوم ≈</option>
                  <option value="none">لم يعمل ✕</option>
                </select>
              </div>
              {status !== 'none' && (
                <div className="detail-box">
                  <label>المشروع</label>
                  <select value={projectId} onChange={(e) => setProjectId(e.target.value)} className="modern-select">
                    <option value="">-- اختر مشروع --</option>
                    {projects.filter(p => p.status === 'active').map(project => (
                      <option key={project.id} value={project.id}>{project.name}</option>
                    ))}
                  </select>
                </div>
              )}
            </div>
          </div>

          {error && <div className="error-message">{error}</div>}

          {/* Summary */}
          {selectedWorkers.length > 0 && startDate && endDate && (
            <div className="bulk-summary">
              <strong>ملخص:</strong> سيتم تسجيل {generateDateRange(startDate, endDate).length} يوم 
              لـ {selectedWorkers.length} عامل (إجمالي {generateDateRange(startDate, endDate).length * selectedWorkers.length} سجل)
            </div>
          )}

          <div className="modal-actions-bulk">
            <button type="button" onClick={onClose} className="btn btn-secondary">
              إلغاء
            </button>
            <button type="submit" className="btn btn-success" disabled={loading}>
              {loading ? 'جاري الحفظ...' : '✓ حفظ التسجيلات'}
            </button>
          </div>
        </form>
      </div>

      {/* Worker Selector Modal */}
      {showWorkerSelector && (
        <WorkerSelectorModal
          workers={workers}
          selectedWorkers={selectedWorkers}
          onClose={() => setShowWorkerSelector(false)}
          onSelect={(selected) => setSelectedWorkers(selected)}
        />
      )}
    </div>
  );
}

export default App;